import './addSheet.css';
import { parseMeal, parseMealVoice, transcribeAudio, refineMeal, parseFoodLabel, AiClarifyError, type SkippedImage, type FoodLabel } from '../services/ai';
import { startRecording, blobToBase64, fmtRecTime, MAX_RECORD_MS, type Recorder } from '../lib/audioRec';
import { photoQueue, jobReason, jobKind, classifyError, retryDelay, MAX_AUTO_ATTEMPTS, type PhotoJob, type JobKind } from '../services/photoQueue';
import {
  searchFoods, addCustomFood, listCustomFoods, updateCustomFood, updateNationalFood, fetchCustomFoodById,
  fetchCustomFoodByName,
  fetchNationalFoodByCode,
  fetchNationalFoodByName,
  type Food, type FoodPatch, type FoodPortion,
} from '../services/foods';
import { fetchFavorites, addFavorite, removeFavorite, favItemForLog, type Favorite } from '../services/favorites';
import { saveWeightForDate, fetchWeightForDate, fetchLastWeightBefore } from '../services/weights';
import { isAdmin } from '../services/access';
import {
  addEntry,
  addEntries,
  deleteEntry,
  fetchEntriesForDate,
  sumTotals,
  restoreEntry,
  updateEntry,
  eatenAtForDay,
  fetchRecentGrouped,
  fetchFoodStats,
  frequentAt,
  fetchTargets,
  type DailyTargets,
  type DayTotals,
  type Entry,
  type EntryItem,
  type FoodStat,
  type FoodStats,
  type MealType,
  type NewEntry,
} from '../services/entries';
import { fetchMealPlan } from '../services/mealPlan';
import { fetchRegularsSource, type RegularsSource } from '../services/regulars';
import { foodArtSvg } from '../lib/foodArt';
import {
  amountText, buildRegulars, convertQty, orderFavorites, isGram, pickMacros, stepDown, stepUp, toEntryItems,
  type Pick as RegPick, type Regular,
} from '../lib/regulars';
import {
  applyOption,
  changeQty,
  countedPicks,
  maxQty,
  optionIsActive,
  selectionToItems,
  selectionTotals,
  toggleComponent,
  type MealPlan,
  type PlanComponent,
  type PlanSection,
  type PlanTotals,
  type Selection,
} from '../lib/mealPlan';
import { escapeHtml, toast, undoToast } from '../lib/ui';
import { reportError } from '../lib/sentry';
import { isTransient } from '../lib/netRetry';
import { foodEmoji, FOOD_ICON_CHOICES } from '../lib/foodEmoji';

const fmt = (n: number): string => Math.round(n).toLocaleString('he-IL');
const round1 = (n: number): number => Math.round(n * 10) / 10;

// נעילת שמירה — מונע כפילות מלחיצה כפולה על שמירה/מועדף/מאכל אישי
let busySaving = false;
/** רינדור מסך האישור הפתוח — כדי ש-saveConfirm יצייר אותו מחדש במצב "שומרת" ובסיומו.
 *  ‏null כשאין מסך אישור פתוח. מתאפס בסגירת הזרימה. */
let renderConfirm: (() => void) | null = null;
// מטמון פריטים במסך החיפוש (מיפוי אינדקס → Entry) — הצעות ותוצאות
let suggestionCache: Entry[] = [];

/**
 * מיפוי שגיאות ה-AI להודעה שאומרת **מה קרה ובאשמת מי** — ומה אפשר לעשות.
 * ⚠️ עד 28.09.2026 עומס אצל גוגל הוצג כ"לא הצלחתי לחשב — נסי החלפת תמונה או תיאור",
 * ואנסטסיה ניסתה שוב ושוב תמונות ותיאורים אחרים לתקלה שלא הייתה שלה. כל הודעה על תקלה
 * שאינה בקלט אומרת את זה במפורש; ההודעה הגנרית נשארה רק לתשובה לא-תקינה מהמודל.
 */
function aiErrorMsg(e: unknown): string {
  // המודל ענה שאלה ולא נכשל — מציגים את מה שהוא אמר, לא הודעה גנרית
  if (e instanceof AiClarifyError) return e.clarify;
  const m = e instanceof Error ? e.message : '';
  if (m === 'ai_busy') return 'שירות ה-AI של גוגל עמוס כרגע — התקלה אצלם, לא בתמונה או בתיאור. נסי שוב בעוד כמה דקות, או הוסיפי מהחיפוש';
  if (m === 'ai_timeout') return 'שירות ה-AI של גוגל לא ענה בזמן — התקלה אצלם, לא בתמונה או בתיאור. נסי שוב בעוד רגע';
  if (m === 'rate_limited') return 'נגמרה מכסת השימוש בשירות ה-AI לעכשיו — זו מגבלה של החשבון, לא בתמונה. נסי שוב מאוחר יותר';
  if (m === 'interrupted') return 'הניתוח נקטע כי יצאת מהאפליקציה באמצע — iPhone עוצר בקשות ברקע. נסי שוב ולהישאר באפליקציה עד שיסתיים';
  if (m === 'network') return 'אין חיבור לאינטרנט או שהקליטה חלשה — הבקשה לא נשלחה. נסי שוב כשיש קליטה';
  if (m === 'no_session' || m === 'unauthorized') return 'החיבור לחשבון פג — צאי מהאפליקציה והיכנסי מחדש';
  if (m === 'forbidden') return 'החשבון עוד לא אושר לשימוש ב-AI';
  if (m === 'bad_request') return 'התמונה או ההקלטה לא הגיעו לשרת תקינות — נסי לצלם או להקליט שוב';
  if (m === 'ai_not_configured') return 'שירות ה-AI לא מוגדר בשרת — זו תקלה אצלנו, לא אצלך';
  if (m === 'internal_error' || m.startsWith('http_')) return 'תקלה בשרת של האפליקציה — לא בתמונה או בתיאור. נסי שוב בעוד רגע';
  return 'שירות ה-AI החזיר תשובה לא תקינה — נסי שוב. אם זה חוזר, זו תקלה אצלנו ולא בתמונה';
}

const MEAL_LABEL: Record<MealType, string> = {
  breakfast: 'בוקר',
  lunch: 'צהריים',
  dinner: 'ערב',
  snack: 'נשנוש',
};

/**
 * סוג הארוחה לפי **שעת ההזנה**. ⚠️ זה גובר על הניחוש של המודל בכוונה: הוא מנחש לפי סוג
 * המזון, ולכן חביתה שהוזנה ב-20:10 נחתה ב"ארוחת בוקר" (קרה לאנסטסיה, 16.09.2026).
 * השעון יודע מתי היא אכלה; המודל רק מנחש. ניתן לשינוי בצ'יפים במסך האישור.
 */
function guessMealType(): MealType {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return 'breakfast';
  if (h >= 11 && h < 16) return 'lunch';
  if (h >= 18 && h < 23) return 'dinner';
  return 'snack';
}

// ============================================================
// מצב הזרימה: תפריט = בוטום-שיט; שאר המסכים = עמודים מלאים.
// ניווט = מחסנית frames + סימן היסטוריה יחיד (כפתור-אחורה של הטלפון סוגר עמוד/שלב).
// ============================================================

let onSavedCb: (() => void) | null = null;
let targetDay: Date = new Date(); // היום שאליו מתועדות ההוספות (ניווט בין ימים)
/** סוג ארוחה כפוי — כשנכנסים דרך "הוספת פריט לארוחה" מהבית (⋯). גובר על ניחוש/היסטוריה. */
let forcedMeal: MealType | null = null;

/** מונה ההוספות בסשן הנוכחי (חיפוש v4: כל ✓ נרשם מיד) — מוצג בכותרת עמוד החיפוש. */
let sessionAdds = 0;
/** האם צריך לרענן את הבית בסגירת הזרימה (הוספות מיידיות / שמירה). */
let pendingRefresh = false;

type Frame = () => void;
let stack: Frame[] = [];
let flowOpen = false;
let navBound = false;
/** עולה בכל שינוי מסך/ניווט — המשכי async (AI) בודקים אותו כדי לא לצייר על מסך שהוחלף. */
let flowEpoch = 0;
/** נעילת חזרה — מונע דאבל-טאפ שצורך שתי רשומות היסטוריה. */
let backPending = false;
/** צילום שנפתח מתור הצילומים (מהבית) ועוד לא נשמר. סגירה בלי שמירה מחזירה אותו לתור —
 *  היא חיכתה לניתוח הזה, ו"אחורה" אינו "מחיקה" (מחיקה = ✕ בכרטיס בבית). */
let resumedJob: PhotoJob | null = null;
/** שעת האכילה של הארוחה שהזרימה עוסקת בה, כשהיא ידועה מראש (צילום מהתור = שעת הצילום) */
let flowEatenAt: string | null = null;

function sheetOverlay(): HTMLElement | null { return document.getElementById('sheet-overlay'); }
function pageOverlay(): HTMLElement | null { return document.getElementById('add-page'); }

/** בחירה אוטומטית של תוכן שדה בפוקוס + כיבוי תיקון אוטומטי ושורת ההצעות (QuickType).
 *  רץ סינכרונית לפני ה-focus כדי שה-iOS יקרא את התכונות בזמן. */
function wireFocusSelect(root: HTMLElement): void {
  root.querySelectorAll<HTMLInputElement>('input[type="number"], input[type="text"]').forEach((inp) =>
    inp.addEventListener('focus', () => inp.select()),
  );
  // מקלדת נקייה: בלי תיקון אוטומטי ובלי שורת ההצעות מעל המקלדת (בשדות טקסט/חיפוש) — חוץ מטקסט
  // חופשי ל-AI (`data-kb="text"`, ראו cleanKeyboardOnce ב-main.ts).
  root.querySelectorAll('input[type="text"]:not([data-kb="text"]), input[type="search"], textarea:not([data-kb="text"])').forEach((el) => {
    el.setAttribute('autocorrect', 'off');
    el.setAttribute('autocapitalize', 'off');
    el.setAttribute('spellcheck', 'false');
  });
}

// ---------- בוטום-שיט (תפריט האריחים בלבד) ----------

function ensureSheet(): void {
  if (sheetOverlay()) return;
  const el = document.createElement('div');
  el.id = 'sheet-overlay';
  el.innerHTML = `<div class="sheet" role="dialog"><div class="handle"></div><div id="sheet-body"></div></div>`;
  el.addEventListener('click', (e) => { if (e.target === el) goBack(); });
  document.body.appendChild(el);
  attachSwipeDismiss(el.querySelector('.sheet') as HTMLElement);
}

/** גרירה מטה לסגירה — לשיט האריחים בלבד */
function attachSwipeDismiss(sheet: HTMLElement): void {
  let startY = 0;
  let dragging = false;
  const onStart = (y: number, fromHandle: boolean) => {
    if (!fromHandle && sheet.scrollTop > 0) return;
    startY = y; dragging = true;
    sheet.style.transition = 'none';
  };
  const onMove = (y: number, e: TouchEvent) => {
    if (!dragging) return;
    const dy = y - startY;
    if (dy <= 0) { sheet.style.transform = ''; return; }
    if (sheet.scrollTop > 0) { dragging = false; sheet.style.transform = ''; return; }
    e.preventDefault();
    sheet.style.transform = `translateY(${dy}px)`;
  };
  const onEnd = (y: number) => {
    if (!dragging) return;
    dragging = false;
    const dy = y - startY;
    sheet.style.transition = 'transform .2s ease-out';
    if (dy > 110) goBack(); else sheet.style.transform = '';
  };
  const handle = sheet.querySelector('.handle') as HTMLElement;
  handle.addEventListener('touchstart', (e) => onStart(e.touches[0].clientY, true), { passive: true });
  sheet.addEventListener('touchstart', (e) => onStart(e.touches[0].clientY, false), { passive: true });
  sheet.addEventListener('touchmove', (e) => onMove(e.touches[0].clientY, e), { passive: false });
  sheet.addEventListener('touchend', (e) => onEnd(e.changedTouches[0].clientY));
}

/** רינדור תוכן השיט (התפריט). מסתיר את עמוד המסך המלא אם היה פתוח. */
function setSheetBody(html: string): HTMLElement {
  closePage();
  ensureSheet();
  const body = document.getElementById('sheet-body')!;
  body.innerHTML = html;
  wireFocusSelect(body);
  return body;
}

// ---------- עמוד מלא (כל שאר המסכים) ----------

/** חץ חזרה שפונה החוצה (ימינה ב-RTL) — SVG שלא מתהפך ע"י ה-bidi כמו התו ‹ */
export const BACK_ARROW_SVG =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';

function ensurePage(): void {
  if (pageOverlay()) return;
  const el = document.createElement('div');
  el.id = 'add-page';
  el.innerHTML =
    `<div class="page-head"><button class="page-back" id="page-back" aria-label="חזרה">${BACK_ARROW_SVG}</button>` +
    `<h3 class="page-title" id="page-title"></h3>` +
    `<div class="page-head-tools" id="page-head-tools"></div></div>` +
    `<div class="page-body" id="page-body"></div>`;
  document.body.appendChild(el);
  el.querySelector('#page-back')!.addEventListener('click', () => goBack());
}

/** רינדור עמוד מלא: כותרת + גוף. מחזיר את גוף העמוד.
 *  מסיר מיידית (בלי אנימציה) את שיט האריחים אם היה פתוח — אחרת הוא נשאר מתחת לעמוד ומציץ
 *  מתחתיו כשהמקלדת נפתחת ומכווצת את #add-page (הוא position:fixed לתחתית ולא מתכווץ איתו). */
function setPage(title: string, html: string): HTMLElement {
  sheetOverlay()?.remove();
  ensurePage();
  document.getElementById('page-title')!.textContent = title;
  document.getElementById('page-head-tools')!.innerHTML = ''; // כלים פר-מסך (מונה/סיום בחיפוש)
  const body = document.getElementById('page-body')!;
  body.innerHTML = html;
  body.scrollTop = 0;
  wireFocusSelect(body);
  return body;
}

function closePage(): void { pageOverlay()?.remove(); }
function closeSheetOverlay(): void {
  const el = sheetOverlay();
  if (!el) return;
  el.classList.add('closing');
  window.setTimeout(() => el.remove(), 200);
}

/** סגירת הזרימה כולה — מסירה שיט+עמוד, מנקה מחסנית, וצורכת את סימן ההיסטוריה.
 *  אם היו הוספות/שמירות (pendingRefresh) — מרענן את הבית פעם אחת. */
function closeAddFlow(consumeHistory = true): void {
  closePage();
  closeSheetOverlay();
  stack = [];
  flowEpoch++;
  forcedMeal = null;
  renderConfirm = null;
  flowEatenAt = null;
  if (resumedJob) { photoQueue.restore(resumedJob); resumedJob = null; }
  cancelRecording();
  // ⚠️ busySaving נשאר דלוק אם המשתמשת סגרה בזמן שמירה, וה-✓ בזרימה הבאה היה מת בשקט
  // (הלחיצה נבלעת ב-`if (busySaving) return`). השמירה שרצה ברקע תסתיים בכל מקרה.
  busySaving = false;
  const wasOpen = flowOpen;
  flowOpen = false;
  if (consumeHistory && wasOpen) history.back(); // צורך את סימן ההיסטוריה (popstate יראה flowOpen=false)
  if (pendingRefresh) {
    pendingRefresh = false;
    // נשמר משהו — "כמות מהפעם הקודמת" ו"תדירים" צריכים לכלול אותו בפתיחה הבאה
    foodStats = null;
    onSavedCb?.();
  }
}

// ---------- ניווט ----------

function bindNavOnce(): void {
  if (navBound) return;
  navBound = true;
  window.addEventListener('popstate', () => {
    backPending = false;
    if (!flowOpen) return;
    flowEpoch++; // המסך משתנה — המשכי async תלויים לא יציירו
    if (stack.length > 1) {
      stack.pop();
      history.pushState({ lbAdd: 1 }, ''); // חימוש מחדש כדי שהאחורה הבא ימשיך לתפוס
      stack[stack.length - 1]();            // רינדור השלב הקודם
    } else {
      closeAddFlow(false); // בשורש → סגירה (ה-popstate כבר צרך את הסימן)
    }
  });
}

/** מעבר לעמוד/מסך חדש (דחיפה למחסנית). */
function navTo(frame: Frame): void {
  flowEpoch++;
  stack.push(frame);
  frame();
}

/** החלפת המסך הנוכחי (בלי להוסיף שלב) — למשל אחרי שמירת מאכל שלי → חיפוש. */
function replaceWith(frame: Frame): void {
  if (stack.length) stack.pop();
  navTo(frame);
}

/** חזרה שלב אחד — עובר דרך ה-History כדי לאחד עם כפתור-אחורה של הטלפון.
 *  נעול נגד דאבל-טאפ (שתי קריאות history.back לפני ה-popstate הראשון). */
function goBack(): void {
  if (!flowOpen) { closeAddFlow(false); return; }
  if (backPending) return;
  backPending = true;
  history.back();
}

// ---------- נקודות כניסה ----------

/** סגירת זרימת ההוספה מבחוץ — main.ts קורא לזה ב-SIGNED_OUT. ‏#add-page הוא fixed עם
 *  z-index 90 ורקע אטום, ולכן בלי זה הוא נשאר פרוס מעל מסך ההתחברות וחוסם אותו לגמרי. */
export function closeAddFlowExternal(): void {
  if (flowOpen) closeAddFlow();
  else { closePage(); closeSheetOverlay(); }
}

/** פתיחת תפריט ההוספה. day = היום שאליו יתועדו ההוספות (ברירת מחדל: היום). */
export function openAddSheet(onSaved: () => void, day: Date = new Date()): void {
  bindNavOnce();
  onSavedCb = onSaved;
  targetDay = day;
  sessionAdds = 0;
  pendingRefresh = false;
  backPending = false;
  forcedMeal = null;
  lastQuery = '';      // זרימה חדשה — חיפוש נקי
  searchTab = 'all';   // (בתוך הזרימה הטאב נשמר בין עמודים)
  stack = [];
  flowOpen = true;
  history.pushState({ lbAdd: 1 }, '');
  navTo(showMenu);
}

/** "הוספת פריט לארוחה" מהבית (⋯) — נפתח ישר בחיפוש, עם סוג הארוחה קבוע מראש.
 *  initialTab: איזה טאב בחיפוש פותחים עליו (ברירת מחדל 'all'; 'fav' לכניסה ישירה למועדפים). */
export function openAddForMeal(
  meal: MealType, onSaved: () => void, day: Date = new Date(), initialTab: SearchTab = 'all',
): void {
  bindNavOnce();
  onSavedCb = onSaved;
  targetDay = day;
  sessionAdds = 0;
  pendingRefresh = false;
  backPending = false;
  forcedMeal = meal;
  lastQuery = '';
  searchTab = initialTab;
  stack = [];
  flowOpen = true;
  history.pushState({ lbAdd: 1 }, '');
  navTo(showSearch);
}

/** פתיחת זרימה מהבית (שדה החיפוש ושורת הכפתורים מתחתיו) — בלי תפריט ה-+ ובלי ארוחה כפויה
 *  (נקבעת לפי שעת ההזנה, כמו מה-+). ההוספות ליום המוצג בבית. */
function startHomeFlow(onSaved: () => void, day: Date): void {
  bindNavOnce();
  onSavedCb = onSaved;
  targetDay = day;
  sessionAdds = 0;
  pendingRefresh = false;
  backPending = false;
  forcedMeal = null;
  lastQuery = '';
  searchTab = 'all';
  stack = [];
  flowOpen = true;
  history.pushState({ lbAdd: 1 }, '');
}

/** שדה החיפוש בבית (בר, 02.10.2026: "תוסיף חיפוש ממסך הבית") — ישר לעמוד החיפוש. */
export function openSearchFromHome(onSaved: () => void, day: Date = new Date()): void {
  startHomeFlow(onSaved, day);
  navTo(showSearch);
  // כאן כן focus (בניגוד ל-showSearch עצמו): היא נגעה בשדה חיפוש כדי להקליד. חייב לרוץ
  // סינכרונית בתוך הנגיעה — אחרי await ה-iOS לא פותח מקלדת.
  document.getElementById('food-q')?.focus();
}

/** שורת הכפתורים מתחת לחיפוש בבית (בר, 04.10.2026: "מתחת לשורת החיפוש, להוסיף את הכפתורים שיש בפלוס
 *  (כולל קבועים…)") — כל כפתור ישר למסך שלו, אותם מסכים כמו מתפריט ה-+. הצילום — openPhotoFromHome. */
export type HomeAction = 'regulars' | 'text' | 'custom' | 'quick' | 'weight';
export function openHomeAction(act: HomeAction, onSaved: () => void, day: Date = new Date()): void {
  const frames: Record<HomeAction, Frame> = {
    regulars: showRegulars,
    text: () => showTextInput(),
    custom: () => showCustomFoodForm(),
    quick: showQuickCal,
    weight: showWeighIn,
  };
  startHomeFlow(onSaved, day);
  navTo(frames[act]);
}

/** צילום מהבית: הבורר נפתח בבית (חייב לרוץ בתוך הנגיעה), והזרימה נפתחת כשנבחרו תמונות — אותה
 *  זרימה כמו "📷 צילום" מה-+ (תצוגה מקדימה → ניתוח → אישור). */
export function openPhotoFromHome(files: File[], onSaved: () => void, day: Date = new Date()): void {
  startHomeFlow(onSaved, day);
  void handlePhoto(files);
}

/** "קלוריות מהירות" לארוחה קבועה מראש (⋯ בבית) — נפתח ישר על מסך הקלט, בלי תפריט. */
export function openQuickCalForMeal(meal: MealType, onSaved: () => void, day: Date = new Date()): void {
  bindNavOnce();
  onSavedCb = onSaved;
  targetDay = day;
  sessionAdds = 0;
  pendingRefresh = false;
  backPending = false;
  forcedMeal = meal;
  stack = [];
  flowOpen = true;
  history.pushState({ lbAdd: 1 }, '');
  navTo(showQuickCal);
}

/** הרישום שנערך כרגע — נשמר למחיקה-עם-ביטול מתוך מסך העריכה. */
let editingEntry: Entry | null = null;

/** פתיחה ישירה לעריכת ארוחה קיימת (מהבית) — עמוד אישור, בלי תפריט. */
export function openEditEntry(entry: Entry, onSaved: () => void): void {
  bindNavOnce();
  onSavedCb = onSaved;
  targetDay = new Date(entry.eaten_at);
  sessionAdds = 0;
  pendingRefresh = false;
  backPending = false;
  forcedMeal = null;
  editingEntry = entry;
  stack = [];
  flowOpen = true;
  history.pushState({ lbAdd: 1 }, '');
  navTo(() => showConfirm({
    items: entry.items?.length ? entry.items : [{
      name_he: entry.description ?? 'ארוחה', calories: entry.calories,
      protein_g: entry.protein_g ?? undefined, carbs_g: entry.carbs_g ?? undefined, fat_g: entry.fat_g ?? undefined,
    }],
    mealType: (entry.meal_type ?? guessMealType()) as MealType,
    clarify: null,
    description: entry.description ?? '',
    source: (entry.source as NewEntry['source']) ?? 'manual',
    editId: entry.id,
    eatenAtIso: entry.eaten_at,
  }));
}

/** רישום חד-פריטי → RowFood לעמוד המאכל (עריכת מנה בודדת מהבית). */
function entryToRow(entry: Entry): RowFood {
  const it = entry.items[0];
  const g = it.grams && it.grams > 0 ? it.grams : 0;
  const perGram = g > 0
    ? { cal: (it.calories ?? 0) / g, p: (it.protein_g ?? 0) / g, c: (it.carbs_g ?? 0) / g, f: (it.fat_g ?? 0) / g }
    : null;
  const units = perGram
    ? [{ label: 'גרם', grams: 1 }, ...(it.unit_grams && it.unit_grams > 1 && it.unit_name ? [{ label: it.unit_name, grams: it.unit_grams }] : [])]
    : [{ label: it.qty_desc || 'מנה', grams: 0 }];
  return {
    kind: 'food',
    name: it.name_he,
    perGram,
    base: { ...it },
    units,
    defMeal: (entry.meal_type ?? 'snack') as MealType,
    sub: '',
  };
}

/** עריכת מנה בודדת מתוך ארוחה — אותו עמוד מאכל כמו בבחירה מהחיפוש, ✓ שומר את השינוי. */
export function openEditItem(entry: Entry, onSaved: () => void): void {
  bindNavOnce();
  onSavedCb = onSaved;
  targetDay = new Date(entry.eaten_at);
  sessionAdds = 0;
  pendingRefresh = false;
  backPending = false;
  forcedMeal = null;
  editingEntry = entry;
  stack = [];
  flowOpen = true;
  history.pushState({ lbAdd: 1 }, '');
  navTo(() => showFoodPage(entryToRow(entry), { id: entry.id }));
}

/**
 * ניתוח מהתור (כרטיס בבית): מוכן → ישר למסך האישור; שאלה מהמודל → למסך הקלט עם השאלה
 * (צילום: ליד שדה הרמז; תיאור: מתחת לטקסט). הארוחה נרשמת לשעת השליחה, לא לשעת האישור.
 */
export function openPhotoJob(jobId: string, onSaved: () => void): void {
  const job = photoQueue.get(jobId);
  if (!job || !((job.status === 'ready' && job.result) || job.status === 'clarify')) return;
  photoQueue.take(jobId);
  bindNavOnce();
  onSavedCb = onSaved;
  targetDay = new Date(job.eatenAtIso);
  sessionAdds = 0;
  pendingRefresh = false;
  backPending = false;
  forcedMeal = job.mealType;
  flowEatenAt = job.eatenAtIso;
  resumedJob = job;
  stack = [];
  flowOpen = true;
  history.pushState({ lbAdd: 1 }, '');
  if (job.status === 'ready') {
    navTo(() => showConfirm(jobConfirmInit(job)));
    return;
  }
  // שאלה על תיאור / הקלטה → מסך התיאור עם השאלה (והטקסט שנשלח, לעריכה)
  if (jobKind(job) !== 'photo') {
    const k = jobKind(job);
    navTo(() => showTextInput(k === 'text' ? job.text ?? '' : ''));
    const err = document.getElementById('sheet-err');
    if (err) err.textContent = job.clarify ?? '';
    return;
  }
  pendingPhotos = job.photos.map((p) => ({
    base64: p.base64, mime: p.mime, hint: p.hint ?? '', dataUrl: `data:${p.mime};base64,${p.base64}`,
  }));
  photoIndex = 0;
  photoClarify = job.clarify ?? null;
  navTo(showPhotoPreview);
}

/** סגירה חיצונית (תאימות) */
export function closeSheet(): void { closeAddFlow(); }

// ============================================================
// 1. תפריט האריחים (בוטום-שיט) — נשאר שיט, בלי רשימות מתחת
// ============================================================

function showMenu(): void {
  const body = setSheetBody(`
    <h3>מה מוסיפים? ✨</h3>
    <div class="opt-grid">
      <button class="opt-tile wide regulars" data-act="regulars">
        <div class="ic">⚡</div><div class="t">הקבועים שלי</div><div class="s">המועדפים שלך — כמה יחד בלחיצה אחת</div>
      </button>
      <button class="opt-tile primary" data-act="photo">
        <div class="ic">📷</div><div class="t">צילום</div><div class="s">צלחת או תווית מזון</div>
      </button>
      <button class="opt-tile" data-act="text">
        <div class="ic">✏️</div><div class="t">תיאור בטקסט</div><div class="s">"אכלתי קוטג' עם לחם…"</div>
      </button>
      <button class="opt-tile" data-act="manual">
        <div class="ic">🔍</div><div class="t">חיפוש במאגר</div><div class="s">מאכלים · מועדפים · אחרונות</div>
      </button>
      <button class="opt-tile" data-act="custom">
        <div class="ic">➕</div><div class="t">מאכל שלי</div><div class="s">הוספה למאגר האישי</div>
      </button>
      <button class="opt-tile" data-act="weight">
        <div class="ic">⚖️</div><div class="t">שקילת בוקר</div><div class="s">עדכון משקל יומי</div>
      </button>
      <button class="opt-tile" data-act="quickcal">
        <div class="ic">🔢</div><div class="t">קלוריות מהירות</div><div class="s">מספר בלי מאכל</div>
      </button>
    </div>
    <input type="file" id="photo-input" accept="image/*" multiple hidden>`);

  body.querySelector('[data-act="photo"]')!.addEventListener('click', () => {
    (document.getElementById('photo-input') as HTMLInputElement).click();
  });
  body.querySelector('#photo-input')!.addEventListener('change', (e) => {
    const files = (e.target as HTMLInputElement).files;
    if (files?.length) void handlePhoto(files);
  });
  body.querySelector('[data-act="regulars"]')!.addEventListener('click', () => navTo(showRegulars));
  body.querySelector('[data-act="text"]')!.addEventListener('click', () => navTo(showTextInput));
  body.querySelector('[data-act="manual"]')!.addEventListener('click', () => navTo(showSearch));
  body.querySelector('[data-act="custom"]')!.addEventListener('click', () => navTo(showCustomFoodForm));
  body.querySelector('[data-act="weight"]')!.addEventListener('click', () => navTo(showWeighIn));
  body.querySelector('[data-act="quickcal"]')!.addEventListener('click', () => navTo(showQuickCal));
  // "התפריט שלי" מוסתר (בר, 29.09.2026: "תוריד את התפריט שלי — אולי נחזיר בהמשך"). הקוד, השירות
  // והנתונים נשארים; להחזרה — להחזיר את הקריאה הזו.
  // void addMealPlanTile(body);
}

/** אריח "התפריט שלי" — נוסף בראש הרשת רק למי שיש לו תפריט (כמו הכניסות המותנות בהגדרות).
 *  התפריט מוחזק בקאש בשירות, ולכן חזרה לתפריט ההוספה לא מהבהבת. */
async function addMealPlanTile(body: HTMLElement): Promise<void> {
  const ep = flowEpoch;
  let plan: MealPlan | null = null;
  try { plan = await fetchMealPlan(); } catch { return; }
  if (!plan || !flowOpen || ep !== flowEpoch) return;
  const grid = body.querySelector('.opt-grid');
  if (!grid?.isConnected || grid.querySelector('[data-act="plan"]')) return;
  grid.insertAdjacentHTML('afterbegin', `
    <button class="opt-tile wide plan" data-act="plan">
      <div class="ic">📋</div><div class="t">${escapeHtml(plan.title)}</div>
      <div class="s">הארוחות שהוכנו לך — לסמן ולהוסיף</div>
    </button>`);
  const p = plan;
  grid.querySelector('[data-act="plan"]')!.addEventListener('click', () => navTo(() => showMealPlan(p)));
}

// ============================================================
// "הקבועים שלי" (הצעה 1, 29.09.2026): רשת של המאכלים שהיא חוזרת עליהם. נגיעה = הכמות
// הרגילה, נגיעה נוספת = עוד מנה, − / ✕ / החלפת יחידה, ו"סל" למטה שרושם הכול בלחיצה אחת.
// שום דבר לא נרשם לפני הכפתור, ואחריו יש ביטול. הלוגיקה עצמה: lib/regulars.ts.
// ============================================================

/** אייקון של קבוע: שנבחר ידנית (אימוג'י) גובר; אחרת האייקון המצויר לפי השם (lib/foodArt.ts); אחרת אימוג'י.
 *  ה-SVG הוא מחרוזת קבועה שלנו — בטוח ל-innerHTML. */
function regIconHtml(r: Regular, cls = 'ic'): string {
  if (r.icon) return `<span class="${cls}">${escapeHtml(r.icon)}</span>`;
  const svg = foodArtSvg(r.name);
  return svg ? `<span class="${cls} art">${svg}</span>` : `<span class="${cls}">${escapeHtml(foodEmoji(r.name))}</span>`;
}

const X_SVG = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const MEAL_ORDER: MealType[] = ['breakfast', 'lunch', 'dinner', 'snack'];

function showRegulars(): void {
  const ep = flowEpoch;
  let meal: MealType = forcedMeal ?? guessMealType();
  let regs: Regular[] | null = null;
  let src: RegularsSource | null = null;
  let failed = false;
  // הסדר תלוי בארוחה שנבחרה (ביצים בערב → ראשונות ב"ערב") — מחושב מחדש בכל החלפה, בלי רשת
  const sortRegs = () => { if (src) regs = buildRegulars(src.favs, src.stats, new Date().getHours(), meal); };
  const sel = new Map<string, RegPick>();   // לפי key; הסדר = סדר הבחירה (Map שומר סדר הכנסה)

  const onPage = () => flowOpen && flowEpoch === ep;
  const body = setPage('הקבועים שלי ⚡', `<div class="sheet-loading"><div class="sheet-spinner"></div><p>טוענת את הקבועים שלך…</p></div>`);
  const tools = document.getElementById('page-head-tools')!;

  const renderTools = () => {
    tools.innerHTML = `<button class="rg-meal" id="rg-meal" aria-label="החלפת ארוחה">ל${MEAL_LABEL[meal]} ▾</button>`;
    tools.querySelector('#rg-meal')!.addEventListener('click', () => {
      meal = MEAL_ORDER[(MEAL_ORDER.indexOf(meal) + 1) % MEAL_ORDER.length];
      sortRegs();
      renderTools();
      render();
    });
  };

  const render = () => {
    if (!onPage()) return;
    if (failed) {
      body.innerHTML = `<div class="rg-empty"><p>לא הצלחתי לטעון את הקבועים — בדקי חיבור.</p>
        <button class="btn-primary" id="rg-retry">ניסיון חוזר</button></div>`;
      body.querySelector('#rg-retry')!.addEventListener('click', load);
      return;
    }
    if (!regs) return;
    if (!regs.length) {
      body.innerHTML = `<div class="rg-empty"><div class="ic">⚡</div>
        <p><b>עוד אין לך מועדפים.</b><br>סמני ⭐ בעמוד מאכל או בשמירת ארוחה, והם יופיעו כאן עם הכמות שלך.</p>
        <button class="btn-primary" id="rg-search">חיפוש במאגר</button></div>`;
      body.querySelector('#rg-search')!.addEventListener('click', () => replaceWith(showSearch));
      return;
    }
    let kcal = 0;
    const tiles = regs.map((r, i) => {
      const p = sel.get(r.key);
      const u = r.units[(p ?? r.usual).unit] ?? r.units[0];
      if (p) kcal += pickMacros(r, p).calories;
      const amount = p ? amountText(p.qty, u) : r.sub ?? `הרגיל: ${amountText(r.usual.qty, r.units[r.usual.unit] ?? u)}`;
      return `
        <div class="rg-tile${p ? ' on' : ''}">
          <button class="rg-main" data-rgtap="${i}" aria-label="${escapeHtml(p ? `עוד ${u.name} ${r.name}` : `הוספת ${r.name}`)}">
            ${regIconHtml(r)}
            <span class="n">${escapeHtml(r.label)}</span>
            <span class="a">${escapeHtml(amount)}</span>
          </button>
          ${p ? `
            <button class="rg-x" data-rgx="${i}" aria-label="${escapeHtml(`הסרת ${r.name}`)}">${X_SVG}</button>
            <div class="rg-ctl">
              <button data-rgminus="${i}" aria-label="פחות">−</button>
              ${r.units.length > 1 ? `<button class="u" data-rgunit="${i}" aria-label="החלפת יחידה">${escapeHtml(isGram(u) ? 'גרם' : u.name)} ⇄</button>` : ''}
            </div>` : ''}
        </div>`;
    }).join('');
    const picked = [...sel.entries()].map(([key, p]) => {
      const r = regs!.find((x) => x.key === key)!;
      const u = r.units[p.unit] ?? r.units[0];
      return `<button class="rg-chip" data-rgchip="${escapeHtml(key)}" aria-label="${escapeHtml(`הסרת ${r.name} מהסל`)}">
        ${regIconHtml(r, 'ci')} ${escapeHtml(amountText(p.qty, u))} <i>✕</i></button>`;
    }).join('');
    const n = sel.size;
    body.innerHTML = `
      <p class="rg-hint">נגיעה = עוד מנה · ✕ הסרה · ⇄ יחידה אחרת</p>
      <div class="rg-grid">${tiles}</div>
      <div class="rg-tray">
        ${n ? `
          <div class="rg-sum"><b>${n === 1 ? 'פריט אחד' : `${n} פריטים`}</b><b>${fmt(kcal)} קק"ל</b></div>
          <div class="rg-chips">${picked}</div>` : '<p class="rg-none">בחרי כמה דברים שאכלת — הכל נכנס בלחיצה אחת</p>'}
        <button class="btn-primary rg-add" id="rg-add"${n && !busySaving ? '' : ' disabled'}>
          ✓ הוספה ל${MEAL_LABEL[meal]}${n ? ` (${n})` : ''}</button>
      </div>`;

    const at = (el: HTMLElement, attr: string) => regs![Number(el.dataset[attr])];
    body.querySelectorAll<HTMLElement>('[data-rgtap]').forEach((b) => b.addEventListener('click', () => {
      const r = at(b, 'rgtap');
      const p = sel.get(r.key);
      if (!p) sel.set(r.key, { ...r.usual });
      else sel.set(r.key, { qty: stepUp(p.qty, r.units[p.unit]), unit: p.unit });
      render();
    }));
    body.querySelectorAll<HTMLElement>('[data-rgminus]').forEach((b) => b.addEventListener('click', () => {
      const r = at(b, 'rgminus');
      const p = sel.get(r.key);
      if (!p) return;
      const q = stepDown(p.qty, r.units[p.unit]);
      if (q > 0) sel.set(r.key, { qty: q, unit: p.unit }); else sel.delete(r.key);
      render();
    }));
    body.querySelectorAll<HTMLElement>('[data-rgunit]').forEach((b) => b.addEventListener('click', () => {
      const r = at(b, 'rgunit');
      const p = sel.get(r.key);
      if (!p) return;
      const next = (p.unit + 1) % r.units.length;
      sel.set(r.key, { qty: convertQty(p.qty, r.units[p.unit], r.units[next]), unit: next });
      render();
    }));
    body.querySelectorAll<HTMLElement>('[data-rgx]').forEach((b) => b.addEventListener('click', () => {
      sel.delete(at(b, 'rgx').key);
      render();
    }));
    body.querySelectorAll<HTMLElement>('[data-rgchip]').forEach((b) => b.addEventListener('click', () => {
      sel.delete(b.dataset.rgchip!);
      render();
    }));
    body.querySelector('#rg-add')!.addEventListener('click', () => void save());
  };

  const save = async () => {
    if (!regs || !sel.size || busySaving) return;
    busySaving = true;
    render();
    // ארוחה מהמועדפים נפרשת לפריטים שלה — כל פריט רישום נפרד, כמו בכל הוספה אחרת
    const chosen = [...sel.entries()].flatMap(([key, p]) => toEntryItems(regs!.find((r) => r.key === key)!, p));
    const mealNow = meal;
    try {
      const ids = await addEntries(chosen.map((it) => ({
        meal_type: mealNow,
        description: it.name_he,
        items: [it],
        ...itemTotals([it]),
        source: 'manual' as const,
      })), flowEatenAt ?? eatenAtForDay(targetDay));
      pendingRefresh = true;
      sel.clear();
      busySaving = false;
      if (onPage()) closeAddFlow();
      else if (!flowOpen) { pendingRefresh = false; onSavedCb?.(); }
      undoToast(`${ids.length === 1 ? 'פריט אחד נוסף' : `${ids.length} פריטים נוספו`} ל${MEAL_LABEL[mealNow]} ✓`, () => {
        void Promise.all(ids.map((id) => deleteEntry(id)))
          .then(() => onSavedCb?.())
          .catch(() => toast('הביטול נכשל', 'error'));
      });
    } catch (e) {
      busySaving = false;
      const transient = isTransient(e);
      reportError(e, { action: 'regulars_add', transient });
      const m = e instanceof Error ? e.message : '';
      toast(m === 'no_session' ? 'הסשן פג — התחברי מחדש'
        : transient ? 'אין חיבור יציב — נסי שוב עוד רגע' : 'ההוספה נכשלה — נסי שוב', 'error');
      render();   // הבחירה נשארת — אפשר ללחוץ שוב
    }
  };

  function load(): void {
    failed = false;
    regs = null;
    body.innerHTML = `<div class="sheet-loading"><div class="sheet-spinner"></div><p>טוענת את הקבועים שלך…</p></div>`;
    fetchRegularsSource()
      .then((s) => { src = s; sortRegs(); render(); })
      .catch(() => { failed = true; render(); });
  }

  renderTools();
  load();
}

// ============================================================
// הוספה מיידית (חיפוש v4): כל ✓ נרשם ישר ליומן + toast עם ביטול
// ============================================================

/** ממיר Entry (מועדף/אחרון/ארוחה) לפריטי EntryItem */
function entryToItems(src: Entry): EntryItem[] {
  return src.items?.length
    ? src.items
    : [{
        name_he: src.description ?? 'ארוחה',
        calories: src.calories,
        protein_g: src.protein_g ?? undefined,
        carbs_g: src.carbs_g ?? undefined,
        fat_g: src.fat_g ?? undefined,
      }];
}

function itemTotals(items: EntryItem[]) {
  return {
    calories: Math.round(items.reduce((s, i) => s + (i.calories ?? 0), 0)),
    protein_g: round1(items.reduce((s, i) => s + (i.protein_g ?? 0), 0)),
    carbs_g: round1(items.reduce((s, i) => s + (i.carbs_g ?? 0), 0)),
    fat_g: round1(items.reduce((s, i) => s + (i.fat_g ?? 0), 0)),
  };
}

/** עדכון מונה ההוספות בכותרת עמוד החיפוש */
function updateSessionCounter(): void {
  const el = document.getElementById('sess-count');
  if (!el) return;
  el.textContent = String(sessionAdds);
  el.classList.toggle('show', sessionAdds > 0);
}

/**
 * רישום מיידי ליומן: **כל מרכיב נשמר כרישום נפרד** (מדיניות: אין ארוחות מרובות-מרכיבים —
 * כל מנה שורה משלה בבית). מעלה מונה לפי מספר המרכיבים ומציג "נוסף ✓ [ביטול]" שמבטל את כולם.
 * נשארים במסך הנוכחי; הבית מרוענן פעם אחת בסגירת הזרימה (pendingRefresh).
 * מחזיר האם נשמר בפועל — כישלון/נעילת-כפילות לא מפעילים המשך (ניקוי/ניווט).
 */
async function instantAdd(items: EntryItem[], mealType: MealType, _description: string, label: string): Promise<boolean> {
  // ⚠️ הריטריי הרחיב את חלון הנעילה משברירי שנייה לשניות. בלי המשוב הזה הלחיצה נעלמת
  // לגמרי — אין טוסט, המונה לא זז, הפריט לא נשמר — במסך שכל ההבטחה שלו היא "✓ נרשם מיד".
  if (busySaving) { toast('רגע, שומרת את הקודם…', 'error'); return false; }
  busySaving = true;
  try {
    const ids = await addEntries(items.map((it) => {
      const t = itemTotals([it]);
      return {
        meal_type: mealType,
        description: it.name_he,
        items: [it],
        calories: t.calories,
        protein_g: t.protein_g,
        carbs_g: t.carbs_g,
        fat_g: t.fat_g,
        source: 'manual',
      };
    }), eatenAtForDay(targetDay));
    sessionAdds += ids.length;
    pendingRefresh = true;
    // הזרימה נסגרה בזמן שהשמירה רצה (הריטריי מאריך אותה) — closeAddFlow כבר קרא את
    // pendingRefresh, ולכן הבית היה נשאר בלי הפריט עד רענון הבא, והמשתמשת מוסיפה שוב.
    if (!flowOpen) onSavedCb?.();
    updateSessionCounter();
    undoToast(`${label} נוסף ל${MEAL_LABEL[mealType]} ✓`, () => {
      void Promise.all(ids.map((id) => deleteEntry(id))).then(() => {
        sessionAdds = Math.max(0, sessionAdds - ids.length);
        updateSessionCounter();
        if (!flowOpen) onSavedCb?.(); // הזרימה כבר נסגרה — לרענן את הבית מיד
      }).catch(() => toast('הביטול נכשל', 'error'));
    });
    return true;
  } catch (e) {
    const transient = isTransient(e);
    reportError(e, { action: 'instant_add', transient });
    const m = e instanceof Error ? e.message : '';
    toast(m === 'no_session' ? 'הסשן פג — התחברי מחדש'
      : transient ? 'אין חיבור יציב — נסי שוב עוד רגע' : 'ההוספה נכשלה — נסי שוב', 'error');
    return false;
  } finally {
    busySaving = false;
  }
}

// ============================================================
// 2. צילום → AI
// ============================================================

/** ‏maxPx קטן יותר כשמצרפים כמה תמונות: זמן הניתוח של Gemini גדל עם שטח התמונות, וזה
 *  המשתנה היחיד שבשליטתנו. נמדד 16.09.2026: ארבע תמונות 1280px = 33–72 שניות; ב-900px = 8–33. */
function compressImage(file: File, maxPx = 1280): Promise<{ base64: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const max = maxPx;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('no_canvas'));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      resolve({ base64: dataUrl.split(',')[1] ?? '', mime: 'image/jpeg' });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('bad_image')); };
    img.src = url;
  });
}

/** תמונה שנבחרה בזרימת הצילום: base64 ל-API, dataUrl לתצוגה, רמז-טקסט נפרד משלה */
interface PendingPhoto { base64: string; mime: string; dataUrl: string; hint: string }
let pendingPhotos: PendingPhoto[] = [];
let photoIndex = 0; // התמונה המוצגת כרגע ב-showPhotoPreview
const MAX_PHOTOS = 4; // תואם MAX_IMAGES בשרת (functions/api/nutri.ts)
/** שאלת הבהרה שהמודל החזיר על הצילום — מוצגת מעל שדה הרמז עד הניתוח הבא */
let photoClarify: string | null = null;

/** דוחסת קבצים ומוסיפה ל-pendingPhotos (עד MAX_PHOTOS); כשל בקובץ בודד לא מפיל את השאר */
async function appendPhotoFiles(files: FileList | File[]): Promise<number> {
  const room = MAX_PHOTOS - pendingPhotos.length;
  const arr = Array.from(files).slice(0, Math.max(0, room));
  // תמונה בודדת נשארת בחדות מלאה; מרגע שיש כמה — מקטינים, אחרת הניתוח חוצה את התקציב
  const total = pendingPhotos.length + arr.length;
  const maxPx = total > 1 ? 960 : 1280;
  const results = await Promise.all(arr.map(async (f) => {
    try {
      const { base64, mime } = await compressImage(f, maxPx);
      return base64 ? { base64, mime, dataUrl: `data:${mime};base64,${base64}`, hint: '' } : null;
    } catch { return null; }
  }));
  const added = results.filter((r): r is PendingPhoto => !!r);
  pendingPhotos.push(...added);
  // ⚠️ כשל חלקי היה שקט: 3 תמונות נבחרו, אחת לא נטענה, ורק השתיים נשלחו לניתוח בלי
  // שנאמר דבר. הודעה מפורשת — המשתמשת צריכה לדעת שמה שהיא בחרה לא כולו נכנס.
  const failed = arr.length - added.length;
  if (failed > 0 && added.length > 0) {
    toast(failed === 1 ? 'תמונה אחת לא נטענה ולא תיכלל' : `${failed} תמונות לא נטענו ולא ייכללו`, 'error');
  }
  // תמונות שלא נכנסו כי עברו את התקרה — גם זה היה שקט
  const overflow = Array.from(files).length - arr.length;
  if (overflow > 0) toast(`עד ${MAX_PHOTOS} תמונות — ${overflow} לא נוספו`, 'error');
  return added.length;
}

/** שומרת את מה שהוקלד בשדה הרמז של התמונה הנוכחית למערך, לפני שמחליפים תמונה/מנתחים */
function flushCurrentHint(): void {
  const ta = document.getElementById('photo-hint') as HTMLTextAreaElement | null;
  if (ta && pendingPhotos[photoIndex]) pendingPhotos[photoIndex].hint = ta.value;
}

async function handlePhoto(files: FileList | File[]): Promise<void> {
  // נכנסים לעמוד טעינה מיד (דוחפים למחסנית), ואז מחליפים לתצוגה מקדימה
  navTo(() => setPage('צילום ארוחה 📷', `<div class="sheet-loading"><div class="sheet-spinner"></div><p>מכינה את התמונה…</p></div>`));
  const ep = flowEpoch; // אם המשתמשת ניווטה/חזרה בזמן הטעינה — לא מציירים על מסך אחר
  pendingPhotos = [];
  photoClarify = null;
  resumedJob = null;   // צילום חדש מחליף את זה שנפתח מהתור (אותה ארוחה — שעת הצילום נשמרת)
  const added = await appendPhotoFiles(files);
  if (!flowOpen || ep !== flowEpoch) return;
  if (!added) {
    goBack();
    toast('לא הצלחתי לטעון את התמונה — נסי שוב', 'error');
    return;
  }
  photoIndex = 0;
  replaceWith(showPhotoPreview);
}

function showPhotoPreview(): void {
  if (!pendingPhotos.length) { goBack(); return; }
  if (photoIndex >= pendingPhotos.length) photoIndex = pendingPhotos.length - 1;
  const n = pendingPhotos.length;
  const cur = pendingPhotos[photoIndex];
  const title = n > 1 ? `צילום ארוחה 📷 (${photoIndex + 1}/${n})` : 'צילום ארוחה 📷';
  const arrows = n > 1 ? `
    <button class="ph-arrow next" id="photo-next" aria-label="התמונה הבאה">‹</button>
    <button class="ph-arrow prev" id="photo-prev" aria-label="התמונה הקודמת">›</button>` : '';
  const dots = n > 1
    ? `<div class="ph-dots">${pendingPhotos.map((_, i) => `<i class="${i === photoIndex ? 'on' : ''}"></i>`).join('')}</div>`
    : '';
  const thumbs = `
    <div class="thumbs">
      ${pendingPhotos.map((p, i) => `<img class="thumb${i === photoIndex ? ' on' : ''}" data-thumb="${i}" src="${p.dataUrl}" alt="תמונה ${i + 1}">`).join('')}
      ${n < MAX_PHOTOS ? '<button class="thumb-add" id="photo-add-more" aria-label="הוספת תמונה">+</button>' : ''}
    </div>`;
  const body = setPage(title, `
    <div class="photo-wrap">
      <img class="photo-preview" src="${cur.dataUrl}" alt="הארוחה שצולמה">
      <button class="ph-del" id="photo-del" aria-label="מחיקת התמונה">✕</button>
      ${arrows}
      ${dots}
    </div>
    ${thumbs}
    ${photoClarify ? `<div class="ai-q">🤔 ${escapeHtml(photoClarify)}</div>` : ''}
    <label class="cf-label">רמז לדיוק${n > 1 ? ' — לתמונה הזו' : ''} <small>(לא חובה)</small>
      <textarea class="sheet-textarea" id="photo-hint" rows="2" data-kb="text" autocorrect="on" autocapitalize="sentences" spellcheck="true" placeholder="למשל: פסטה ברוטב עגבניות, בלי גבינה">${escapeHtml(cur.hint)}</textarea></label>
    <button class="rec-btn" id="hint-rec"><span class="dot"></span><span class="lbl">🎙️ הקלטת רמז במקום להקליד</span></button>
    <div class="sheet-err" id="hint-err"></div>
    <button class="btn-primary" id="photo-analyze">נתחי את ${n > 1 ? `${n} התמונות` : 'התמונה'} ✨</button>
    <button class="btn-link" id="photo-add-more-link">📷 הוספת תמונה נוספת</button>`);

  const goTo = (i: number) => { cancelRecording(); flushCurrentHint(); photoIndex = i; showPhotoPreview(); };
  body.querySelector('#photo-next')?.addEventListener('click', () => goTo((photoIndex + 1) % n));
  body.querySelector('#photo-prev')?.addEventListener('click', () => goTo((photoIndex - 1 + n) % n));
  body.querySelectorAll<HTMLElement>('[data-thumb]').forEach((el) =>
    el.addEventListener('click', () => goTo(Number(el.dataset.thumb))),
  );
  body.querySelector('#photo-del')!.addEventListener('click', () => {
    pendingPhotos.splice(photoIndex, 1);
    if (!pendingPhotos.length) {
      goBack();
      toast('התמונה הוסרה — הצילום בוטל');
      return;
    }
    if (photoIndex >= pendingPhotos.length) photoIndex = pendingPhotos.length - 1;
    showPhotoPreview();
  });
  const addMore = () => {
    if (pendingPhotos.length >= MAX_PHOTOS) { toast(`עד ${MAX_PHOTOS} תמונות בבת אחת`, 'error'); return; }
    flushCurrentHint();
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*'; inp.multiple = true;
    inp.addEventListener('change', () => {
      if (!inp.files?.length) return;
      const startIndex = pendingPhotos.length;
      void appendPhotoFiles(inp.files).then((added) => {
        if (added) photoIndex = startIndex;
        showPhotoPreview();
      });
    });
    inp.click();
  };
  body.querySelector('#photo-add-more')?.addEventListener('click', addMore);
  body.querySelector('#photo-add-more-link')!.addEventListener('click', addMore);
  body.querySelector('#hint-rec')!.addEventListener('click', () =>
    void toggleRecording('hint-rec', 'hint-err', (wav) => void fillHintFromVoice(wav)));
  body.querySelector('#photo-analyze')!.addEventListener('click', () => {
    flushCurrentHint();
    doPhotoAnalyze();
  });
}

/** מילים לפי סוג הניתוח — לכותרות ולהודעות של התור */
const JOB_WORDS: Record<JobKind, { title: string; the: string; saved: string; working: string }> = {
  photo: { title: 'צילום ארוחה 📷', the: 'הצילום', saved: 'הצילום שמור', working: 'המאמנת מנתחת את התמונה…' },
  text: { title: 'תיאור בטקסט ✏️', the: 'התיאור', saved: 'התיאור שמור', working: 'המאמנת מחשבת קלוריות…' },
  voice: { title: 'תיאור בהקלטה 🎙️', the: 'ההקלטה', saved: 'ההקלטה שמורה', working: 'מקשיבה ומחשבת…' },
};

/** ניתוח שהסתיים → מסך האישור. סוג הארוחה והשעה נקבעו בזמן השליחה, לא בזמן האישור. */
function jobConfirmInit(job: PhotoJob): ConfirmInit {
  const meal = job.result!;
  const kind = jobKind(job);
  const p = job.photos[0];
  return {
    items: meal.items,
    mealType: job.mealType,   // בחירה מפורשת > השעה (בזמן השליחה) > (לעולם לא) ניחוש המודל
    clarify: meal.clarify,
    skipped: meal.skipped,
    heard: kind === 'voice' ? meal.heard ?? null : undefined,
    description: kind === 'text' && job.text ? job.text : meal.items.map((i) => i.name_he).join(', '),
    source: kind === 'photo' ? 'photo' : 'ai_text',
    photoUrl: kind === 'photo' && p ? `data:${p.mime};base64,${p.base64}` : undefined,
    eatenAtIso: job.eatenAtIso,
  };
}

/**
 * המסך הנוכחי מחכה לניתוח הזה כל עוד הוא מוצג: מוכן → מסך האישור; שאלה / כשל סופי →
 * onBack (חזרה למסך הקלט, עם השאלה או ההודעה). מרגע שעזבה (סגירה / אחורה) — false, והתוצאה
 * עוברת לכרטיס בבית ולהודעה הגלובלית (main.ts).
 */
function followJob(job: PhotoJob, onBack: (j: PhotoJob, msg: string, isQuestion: boolean) => void): void {
  const ep = flowEpoch;
  renderJobWait(job);
  photoQueue.watch(job.id, (j) => {
    if (!flowOpen || ep !== flowEpoch || !j) return false;
    if (j.status === 'ready') {
      photoQueue.take(j.id);
      // מחליפים את עמוד הקלט בעמוד האישור (חזרה מהאישור → תפריט, לא חזרה לקלט)
      replaceWith(() => showConfirm(jobConfirmInit(j)));
    } else if (j.status === 'clarify') {
      photoQueue.take(j.id);
      onBack(j, j.clarify ?? '', true);
    } else if (j.status === 'failed') {
      photoQueue.take(j.id);
      onBack(j, aiErrorMsg(new Error(j.lastError)), false);
    } else {
      renderJobWait(j);
    }
    return true;
  });
}

/**
 * ⚠️ כל ניתוח צילום עובר דרך התור (services/photoQueue) — 29.09.2026. עד אז עומס בגוגל החזיר
 * אותה לתצוגה המקדימה עם הודעת שגיאה, והיא ניסתה שוב ושוב ידנית ("כמעט ולא מצליחה").
 * עכשיו כשל זמני משאיר את הצילום שמור, והתור ממשיך לנסות לבד; היא יכולה לחכות כאן או לסגור,
 * והתוצאה מחכה לה בבית. שאלת הבהרה וכשל סופי חוזרים לתצוגה המקדימה כמו קודם.
 */
function doPhotoAnalyze(): void {
  if (!pendingPhotos.length) return;
  cancelRecording();   // ניתוח מחליף מסך בלי לסגור את הזרימה — המיקרופון היה נשאר דלוק
  photoClarify = null;   // שאלה קודמת לא נגררת לניתוח הבא
  resumedJob = null;     // ניתוח חדש מחליף את הצילום שנפתח מהתור (הוא נכנס לתור מחדש כאן)
  const job = photoQueue.enqueue({
    kind: 'photo',
    photos: pendingPhotos.map((p) => ({ base64: p.base64, mime: p.mime, hint: p.hint })),
    mealType: forcedMeal ?? guessMealType(),   // בחירה מפורשת > השעה > (לעולם לא) ניחוש המודל
    eatenAtIso: flowEatenAt ?? eatenAtForDay(targetDay),
  });
  followJob(job, (_j, msg, isQuestion) => {
    if (isQuestion) {
      // שאלת הבהרה נשארת על המסך ליד שדה הרמז (טוסט נעלם אחרי 2.6 שניות, והשאלה ארוכה)
      photoClarify = msg;
      showPhotoPreview();
      document.getElementById('photo-hint')?.focus();
    } else {
      showPhotoPreview();
      toast(msg, 'error');
    }
  });
}

/** מסך ההמתנה לניתוח. ניסיון ראשון — ספינר רגיל; אחרי כשל — מה קרה, ושהקלט לא אבד. */
function renderJobWait(job: PhotoJob): void {
  const kind = jobKind(job);
  const w = JOB_WORDS[kind];
  const n = job.photos.length;
  const working = job.status === 'working';
  const first = kind === 'photo' && n > 1 ? 'המאמנת מנתחת את התמונות…' : w.working;
  const body = setPage(w.title, job.attempts === 0 ? `
    <div class="sheet-loading"><div class="sheet-spinner"></div><p>${first}</p></div>
    <button class="btn-link" id="pw-bg">להמשיך בינתיים — התוצאה תחכה לך בבית</button>` : `
    <div class="photo-wait">
      ${working ? '<div class="sheet-spinner"></div>' : '<div class="pw-ico">⏳</div>'}
      <b>${escapeHtml(jobReason(job.lastError, kind))}</b>
      <p>${w.saved} אצלי, ואני ממשיכה לנסות לבד${working ? ' — מנסה עכשיו…' : ''}.
        אפשר לסגור ולהמשיך: כשהניתוח יצליח, הוא יחכה לך לאישור במסך הבית.</p>
    </div>
    <button class="btn-primary" id="pw-bg">סגירה — להמשיך לנסות ברקע</button>
    ${working ? '' : '<button class="btn-link" id="pw-now">לנסות שוב עכשיו</button>'}`);
  body.querySelector('#pw-bg')!.addEventListener('click', () => {
    closeAddFlow();
    toast(`${w.saved} — התוצאה תחכה לך במסך הבית`);
  });
  body.querySelector('#pw-now')?.addEventListener('click', () => photoQueue.retryNow(job.id));
}

/** צילום/בחירה מחדש לגמרי (מבטלת את התמונות הקיימות) — מהתיקון "✏️ תמונה לא מדויקת" במסך האישור */
function pickPhoto(): void {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = 'image/*';
  inp.multiple = true;
  inp.addEventListener('change', () => {
    if (inp.files?.length) void handlePhoto(inp.files);
  });
  inp.click();
}

// ============================================================
// 3. תיאור בטקסט → AI
// ============================================================

/** ההקלטה הפעילה — ברמת מודול, כדי שניווט/סגירה ישחררו את המיקרופון גם באמצע. */
let activeRec: { rec: Recorder; timer: number; startedAt: number } | null = null;

/** שחרור המיקרופון בלי לשלוח. ⚠️ חובה בכל יציאה מהמסך: הקלטה שנשארת פתוחה משאירה
 *  את נורית המיקרופון דלוקה ומרוקנת סוללה. */
function cancelRecording(): void {
  if (!activeRec) return;
  window.clearInterval(activeRec.timer);
  activeRec.rec.cancel();
  activeRec = null;
}

function showTextInput(prefill = ''): void {
  const body = setPage('תיאור בטקסט ✏️', `
    <label class="cf-label">מה אכלת?
      <textarea class="sheet-textarea" id="meal-text" rows="3" data-kb="text" autocorrect="on" autocapitalize="sentences" spellcheck="true"
        placeholder="למשל: קוטג' 5% עם שתי פרוסות לחם מלא וסלט ירקות">${escapeHtml(prefill)}</textarea></label>
    <button class="rec-btn" id="meal-rec"><span class="dot"></span><span class="lbl">🎙️ הקלטה במקום להקליד</span></button>
    <button class="btn-primary" id="meal-parse">חשבי לי קלוריות ✨</button>
    <div class="sheet-err" id="sheet-err"></div>`);
  const ta = body.querySelector<HTMLTextAreaElement>('#meal-text')!;
  if (!prefill) ta.focus();
  body.querySelector('#meal-parse')!.addEventListener('click', () => doParse(ta.value));
  body.querySelector('#meal-rec')!.addEventListener('click', () => void toggleRecording());
}

/**
 * לחיצה ראשונה מתחילה הקלטה, שנייה עוצרת (כמו ב-Shopping). משותף לשני מסכים:
 * ‏btnId/errId — הכפתור ושורת השגיאה של המסך; onWav — מה עושים עם ההקלטה.
 */
async function toggleRecording(
  btnId = 'meal-rec', errId = 'sheet-err', onWav: (wav: Blob) => void = (w) => void doVoiceParse(w),
): Promise<void> {
  const btn = document.getElementById(btnId) as HTMLButtonElement | null;
  const err = document.getElementById(errId);
  const setLbl = (t: string) => { const l = btn?.querySelector('.lbl'); if (l) l.textContent = t; };

  if (activeRec) {
    const { rec, timer } = activeRec;
    window.clearInterval(timer);
    activeRec = null;
    btn?.classList.remove('on');
    onWav(await rec.stop());
    return;
  }
  try {
    const rec = await startRecording();
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const ms = Date.now() - startedAt;
      setLbl(`⏺ מקליטה ${fmtRecTime(ms)} — הקישי לסיום`);
      // תקרה: מעבר לדקה זו כנראה שכחה לעצור, והמטען נעשה כבד לחינם
      if (ms >= MAX_RECORD_MS) void toggleRecording(btnId, errId, onWav);
    }, 250);
    activeRec = { rec, timer, startedAt };
    btn?.classList.add('on');
    setLbl('⏺ מקליטה 0:00 — הקישי לסיום');
    if (err) err.textContent = '';
  } catch {
    if (err) err.textContent = 'אין גישה למיקרופון — בדקי הרשאות בהגדרות הטלפון';
  }
}

/** הקלטת רמז בצילום → תמלול לתוך שדה הרמז (מתווסף למה שכבר נכתב, לא דורס). */
async function fillHintFromVoice(wav: Blob): Promise<void> {
  const btn = document.getElementById('hint-rec') as HTMLButtonElement | null;
  const lbl = btn?.querySelector('.lbl');
  const err = document.getElementById('hint-err');
  const ep = flowEpoch;
  const idx = photoIndex;   // הרמז שייך לתמונה שהייתה מוצגת כשהתחילה להקליט
  if (btn) btn.disabled = true;
  if (lbl) lbl.textContent = '⏳ מתמללת…';
  try {
    const text = await transcribeAudio(await blobToBase64(wav));
    if (!flowOpen || ep !== flowEpoch || !pendingPhotos[idx]) return;
    const prev = pendingPhotos[idx].hint.trim();
    pendingPhotos[idx].hint = prev ? `${prev} ${text}` : text;
    if (photoIndex === idx) {
      const ta = document.getElementById('photo-hint') as HTMLTextAreaElement | null;
      if (ta) ta.value = pendingPhotos[idx].hint;
    }
    if (err) err.textContent = '';
  } catch (e) {
    if (!flowOpen || ep !== flowEpoch) return;
    if (err) err.textContent = aiErrorMsg(e);
  } finally {
    if (btn?.isConnected) {
      btn.disabled = false;
      if (lbl) lbl.textContent = '🎙️ הקלטת רמז במקום להקליד';
    }
  }
}

/** מסך התיאור עם הודעה (שאלת הבהרה / כשל) — הטקסט שהקלידה לא נמחק. */
function backToText(text: string, msg: string): void {
  showTextInput(text);
  const err = document.getElementById('sheet-err');
  if (err) err.textContent = msg;
}

/**
 * ההקלטה → תמלול + פירוק בקריאה אחת, ואז מסך האישור עם מה שנשמע.
 * ⚠️ מ-29.09.2026 דרך תור הניתוחים, כמו צילום: עומס בגוגל לא מאבד את ההקלטה, ואפשר לצאת.
 */
async function doVoiceParse(wav: Blob): Promise<void> {
  setPage('תיאור בהקלטה 🎙️', `<div class="sheet-loading"><div class="sheet-spinner"></div><p>מקשיבה ומחשבת…</p></div>`);
  const ep = flowEpoch;
  let audio: string;
  try {
    audio = await blobToBase64(wav);
  } catch {
    if (flowOpen && ep === flowEpoch) backToText('', 'ההקלטה לא נשמרה — נסי להקליט שוב');
    return;
  }
  if (!flowOpen || ep !== flowEpoch) return;
  resumedJob = null;
  const job = photoQueue.enqueue({
    kind: 'voice',
    audio,
    mealType: forcedMeal ?? guessMealType(),
    eatenAtIso: flowEatenAt ?? eatenAtForDay(targetDay),
  });
  // התמלול נכשל / שאלה — חוזרים למסך הקלט כדי שאפשר יהיה להקליד במקום
  followJob(job, (_j, msg) => backToText('', msg));
}

/** ⚠️ מ-29.09.2026 דרך תור הניתוחים, כמו צילום — ראו doPhotoAnalyze. */
function doParse(text: string): void {
  cancelRecording();   // כנ"ל — "חשבי" בזמן הקלטה
  const trimmed = text.trim();
  if (trimmed.length < 3) {
    const err = document.getElementById('sheet-err');
    if (err) err.textContent = 'ספרי לי מה אכלת קודם 🙂';
    return;
  }
  resumedJob = null;
  const job = photoQueue.enqueue({
    kind: 'text',
    text: trimmed,
    mealType: forcedMeal ?? guessMealType(),   // כנ"ל — ראו ההערה על guessMealType
    eatenAtIso: flowEatenAt ?? eatenAtForDay(targetDay),
  });
  // חשוב: לא מוחקים את הטקסט שהוקלד — משחזרים אותו בשדה, עם השאלה / ההודעה
  followJob(job, (_j, msg) => backToText(trimmed, msg));
}

// ============================================================
// 4. מסך אישור: כמויות (גרמים) + עריכה
// ============================================================

interface ConfirmQty {
  name_he: string;
  qty_desc?: string;
  gramsBased: boolean;
  grams: number;
  refGrams: number;
  factor: number;
  perGram: { calories: number; protein: number; carbs: number; fat: number };
  base: { calories: number; protein: number; carbs: number; fat: number };
  unitGrams?: number;
  unitName?: string;
  icon?: string;
}

interface ConfirmState {
  qtys: ConfirmQty[];
  mealType: MealType;
  clarify: string | null;
  skipped?: SkippedImage[];
  heard?: string | null;
  description: string;
  source: NewEntry['source'];
  editId?: string;
  eatenAtIso?: string;
  photoUrl?: string;
  /** ⭐ במסך האישור. ארוחה של כמה פריטים: כארוחה אחת (תבנית) או כל מרכיב כמועדף נפרד — שני דברים
   *  שונים ב"הקבועים שלי" (אריח ארוחה מול אריח לכל מרכיב). בר, 29.09.2026. */
  saveFav: false | 'meal' | 'items';
}

interface ConfirmInit {
  items: EntryItem[];
  mealType: MealType;
  clarify: string | null;
  /** תמונות שלא נכללו בחישוב — מוצג כאזהרה, לא כשאלה */
  skipped?: SkippedImage[];
  /** התמלול של הקלטה — מוצג כדי שהמשתמשת תראה מה נשמע */
  heard?: string | null;
  description: string;
  source: NewEntry['source'];
  editId?: string;
  eatenAtIso?: string;
  photoUrl?: string;
  /** ⭐ שסומן לפני תיקון ל-AI — שורד את בניית המסך מחדש */
  saveFav?: ConfirmState['saveFav'];
  /** תיקון שנכשל: תיבת התיקון נפתחת עם הטקסט שהקלידה ועם הסיבה (ביקורת 29.09 — היא הקלידה מחדש שוב ושוב) */
  refineDraft?: string;
  refineError?: string;
}

function toQty(it: EntryItem): ConfirmQty {
  const cal = it.calories ?? 0, p = it.protein_g ?? 0, c = it.carbs_g ?? 0, f = it.fat_g ?? 0;
  const g = it.grams && it.grams > 0 ? it.grams : 0;
  return {
    name_he: it.name_he,
    qty_desc: it.qty_desc,
    gramsBased: g > 0,
    grams: g,
    refGrams: g,
    factor: 1,
    perGram: g > 0
      ? { calories: cal / g, protein: p / g, carbs: c / g, fat: f / g }
      : { calories: 0, protein: 0, carbs: 0, fat: 0 },
    base: { calories: cal, protein: p, carbs: c, fat: f },
    unitGrams: it.unit_grams && it.unit_grams > 0 ? it.unit_grams : undefined,
    unitName: it.unit_name,
    icon: it.icon,
  };
}

function qtyValues(q: ConfirmQty) {
  if (q.gramsBased) {
    return {
      calories: q.perGram.calories * q.grams,
      protein: q.perGram.protein * q.grams,
      carbs: q.perGram.carbs * q.grams,
      fat: q.perGram.fat * q.grams,
    };
  }
  return {
    calories: q.base.calories * q.factor,
    protein: q.base.protein * q.factor,
    carbs: q.base.carbs * q.factor,
    fat: q.base.fat * q.factor,
  };
}

function stateTotals(state: ConfirmState) {
  return state.qtys.reduce(
    (acc, q) => {
      const v = qtyValues(q);
      return {
        calories: acc.calories + v.calories,
        protein: acc.protein + v.protein,
        carbs: acc.carbs + v.carbs,
        fat: acc.fat + v.fat,
      };
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 },
  );
}

/** אזהרה על תמונות שלא נכללו בחישוב. ⚠️ לא `.ai-q` — זו לא שאלה אלא הודעה שמשהו חסר,
 *  והמשתמשת צריכה להחליט אם לצלם שוב. בלי זה התמונה פשוט נעלמת מהסכום. */
function skippedHtml(skipped?: SkippedImage[]): string {
  if (!skipped?.length) return '';
  const rows = skipped
    .map((sk) => `<li>תמונה ${sk.index} — ${escapeHtml(sk.reason)}</li>`)
    .join('');
  const head = skipped.length === 1 ? 'תמונה אחת לא נכללה בחישוב:' : `${skipped.length} תמונות לא נכללו בחישוב:`;
  return `<div class="ai-skipped"><b>⚠️ ${head}</b><ul>${rows}</ul></div>`;
}

function showConfirm(init: ConfirmInit): void {
  const isEdit = !!init.editId;
  const state: ConfirmState = {
    qtys: init.items.map(toQty),
    mealType: init.mealType,
    clarify: init.clarify,
    skipped: init.skipped,
    heard: init.heard,
    description: init.description,
    source: init.source,
    editId: init.editId,
    eatenAtIso: init.eatenAtIso,
    photoUrl: init.photoUrl,
    saveFav: init.saveFav ?? false,
  };

  const refreshNumbers = () => {
    const body = document.getElementById('page-body');
    if (!body) return;
    state.qtys.forEach((q, i) => {
      const kc = body.querySelector(`[data-kc="${i}"]`);
      if (kc) kc.textContent = fmt(qtyValues(q).calories);
    });
    const totalEl = body.querySelector('[data-total]');
    if (totalEl) totalEl.textContent = `${fmt(stateTotals(state).calories)} קק"ל`;
  };

  const title = isEdit ? 'עריכת ארוחה ✏️' : (init.source === 'photo' ? 'זיהיתי בצלחת 🍽️' : 'הארוחה שלך 🍽️');
  let firstRender = true;
  // טקסט התיקון וההודעה מוצגים רק ברינדור הראשון — +/− אחר כך לא יפתח את התיבה מחדש
  let firstRefine = true;

  const render = () => {
    const rows = state.qtys
      .map((q, i) => {
        const v = qtyValues(q);
        const control = q.gramsBased
          ? `<div class="stepper grams">
               <button class="pm" data-minus="${i}" aria-label="פחות">−</button>
               <input class="grams-input" type="number" inputmode="numeric" min="1" max="5000" step="1"
                 value="${Math.round(q.grams)}" data-g="${i}" aria-label="גרמים ${escapeHtml(q.name_he)}">
               <span class="grams-unit">ג'</span>
               <button class="pm" data-plus="${i}" aria-label="עוד">+</button>
             </div>`
          : `<div class="stepper">
               <button class="pm" data-minus="${i}" aria-label="פחות">−</button>
               <b>${q.qty_desc ? escapeHtml(q.qty_desc) : 'מנה'}${q.factor !== 1 ? ` ×${q.factor}` : ''}</b>
               <button class="pm" data-plus="${i}" aria-label="עוד">+</button>
             </div>`;
        const sub = q.gramsBased && q.unitGrams && q.unitName
          ? `<small>${fmt(q.grams / q.unitGrams)} × ${escapeHtml(q.unitName)}</small>`
          : (q.qty_desc && q.gramsBased ? `<small>${escapeHtml(q.qty_desc)}</small>` : '');
        return `
        <div class="item-row">
          <span class="ir-em">${q.icon || foodEmoji(q.name_he)}</span>
          <div class="n">${escapeHtml(q.name_he)}${sub}</div>
          ${control}
          <div class="kc" data-kc="${i}">${fmt(v.calories)}</div>
          <button class="item-del" data-itemdel="${i}" aria-label="הסרת ${escapeHtml(q.name_he)}">✕</button>
        </div>`;
      })
      .join('');
    const t = stateTotals(state);
    const mealBtns = (Object.keys(MEAL_LABEL) as MealType[])
      .map((m) => `<button class="chip${m === state.mealType ? ' on' : ''}" data-meal="${m}">${MEAL_LABEL[m]}</button>`)
      .join('');

    const showQtyChips = state.clarify && state.qtys.length === 1;
    const q0 = state.qtys[0];
    const chipActive = (val: number) => (q0?.gramsBased ? q0.grams === Math.round(q0.refGrams * val) : q0?.factor === val);
    const qtyChips = showQtyChips
      ? `<div class="qty-chips">${[0.5, 1, 1.5, 2]
          .map((val) => `<button class="chip${chipActive(val) ? ' on' : ''}" data-qty="${val}">${val === 1 ? 'רגיל' : `×${val}`}</button>`)
          .join('')}</div>`
      : '';

    const canRefine = state.source === 'ai_text' || state.source === 'photo';
    const emptyItems = state.qtys.length === 0;

    // setPage מאפס את הגלילה — נכון בכניסה, אבל כאן כל +/−/ארוחה/⭐ בונה את המסך מחדש, והיא
    // נזרקה לראש באמצע תיקון פריט אחרון (ביקורת 29.09). מחזירים את המיקום אחרי הבנייה (מיקרו-משימה:
    // רצה אחרי סוף render ולפני ציור).
    const keepScroll = firstRender ? 0 : (document.getElementById('page-body')?.scrollTop ?? 0);
    firstRefine = firstRender;
    firstRender = false;
    if (keepScroll) queueMicrotask(() => { const b = document.getElementById('page-body'); if (b) b.scrollTop = keepScroll; });

    setPage(title, `
      ${state.photoUrl ? `<img class="photo-thumb" src="${state.photoUrl}" alt="הארוחה שצולמה">` : ''}
      ${state.clarify ? `<div class="ai-q">🤔 ${escapeHtml(state.clarify)}</div>${qtyChips}` : ''}
      ${state.heard ? `<div class="ai-heard">🎙️ שמעתי: “${escapeHtml(state.heard)}”</div>` : ''}
      ${skippedHtml(state.skipped)}
      <div class="items">${rows}</div>
      ${emptyItems ? '<div class="items-empty muted">אין פריטים — חזרי והוסיפי דרך חיפוש</div>' : ''}
      <div class="total"><span>סה"כ</span><span data-total>${fmt(t.calories)} קק"ל</span></div>
      ${canRefine ? `
        <button class="btn-link refine-open" id="refine-open">✏️ משהו לא מדויק? תקני ל-AI</button>
        <div class="refine-box" id="refine-box"${init.refineDraft && firstRefine ? '' : ' hidden'}>
          ${init.refineError && firstRefine ? `<div class="sheet-err">${escapeHtml(init.refineError)}</div>` : ''}
          <input class="sheet-input" id="refine-text" data-kb="text" autocorrect="on" autocapitalize="sentences" spellcheck="true" placeholder="למשל: זה קוסקוס לא אורז, ובלי רוטב"
            value="${init.refineDraft && firstRefine ? escapeHtml(init.refineDraft) : ''}">
          <div class="refine-actions">
            <button class="btn-primary" id="refine-send">שלחי תיקון</button>
            ${state.source === 'photo' ? '<button class="btn-link" id="refine-rephoto">📷 צילום חדש</button>' : ''}
          </div>
        </div>` : ''}
      <div class="meal-type-row">${mealBtns}</div>
      <button class="fav-toggle${state.saveFav ? ' on' : ''}" id="fav-toggle">${state.saveFav ? '⭐ יישמר במועדפים' : '☆ שמירה במועדפים'}</button>
      ${state.saveFav && state.qtys.length > 1 ? `
        <div class="fav-mode" role="radiogroup" aria-label="איך לשמור במועדפים">
          <button class="${state.saveFav === 'items' ? 'on' : ''}" data-favmode="items" role="radio" aria-checked="${state.saveFav === 'items'}">כל מרכיב בנפרד</button>
          <button class="${state.saveFav === 'meal' ? 'on' : ''}" data-favmode="meal" role="radio" aria-checked="${state.saveFav === 'meal'}">כארוחה אחת</button>
        </div>` : ''}
      ${isEdit ? '<button class="btn-link entry-del" id="confirm-del">🗑 מחיקת הרישום</button>' : ''}`);

    // ✓ בכותרת (לא בתחתית הגוף) — נשאר בהישג יד גם כשיש הרבה מרכיבים והמקלדת פתוחה
    // וצריך לגלול; setPage מנקה את page-head-tools בכל render, אז מחווטים כאן מחדש בכל פעם.
    const tools = document.getElementById('page-head-tools')!;
    // ⚠️ הכפתור נבנה מחדש בכל render — ולכן חייב לקרוא את busySaving כאן. בלי זה כל נגיעה
    // במסך בזמן הריטריי (‏+/−, סוג ארוחה, מחיקת פריט) הייתה מחזירה כפתור *מאופשר*, והלחיצה
    // הבאה נבלעת בשקט ב-`if (busySaving) return`.
    tools.innerHTML = `<button class="ha go" id="confirm-save" aria-label="${isEdit ? 'שמירת שינויים' : 'הוספה ליומן'}"${emptyItems || busySaving ? ' disabled' : ''}>✓</button>`;
    tools.querySelector('#confirm-save')!.addEventListener('click', () => void saveConfirm(state));

    const body = document.getElementById('page-body')!;
    // ⚠️ כל עריכה בזמן שמירה תיבלע: saveConfirm צילם את הפריטים לפני ה-await, ולכן שינוי
    // שנעשה תוך כדי הריטריי לא ייכנס לרישום — והמסך עדיין יגיד "נוסף ליומן ✓". חוסמים במקור.
    body.classList.toggle('saving', busySaving);
    body.querySelectorAll<HTMLButtonElement>('[data-itemdel]').forEach((b) =>
      b.addEventListener('click', () => { if (busySaving) return; state.qtys.splice(Number(b.dataset.itemdel), 1); render(); }),
    );
    body.querySelector('#refine-open')?.addEventListener('click', () => {
      const box = document.getElementById('refine-box')!;
      box.hidden = !box.hidden;
      if (!box.hidden) (document.getElementById('refine-text') as HTMLInputElement).focus();
    });
    body.querySelector('#refine-send')?.addEventListener('click', () => {
      const txt = (document.getElementById('refine-text') as HTMLInputElement).value.trim();
      if (txt) void doRefine(state, txt);
    });
    body.querySelector('#refine-rephoto')?.addEventListener('click', () => pickPhoto());
    body.querySelectorAll<HTMLButtonElement>('[data-qty]').forEach((b) =>
      b.addEventListener('click', () => {
        const val = Number(b.dataset.qty);
        const q = state.qtys[0];
        if (q.gramsBased) q.grams = Math.max(1, Math.round(q.refGrams * val));
        else q.factor = val;
        render();
      }),
    );
    body.querySelectorAll<HTMLInputElement>('.grams-input').forEach((inp) => {
      inp.addEventListener('focus', () => inp.select());
      inp.addEventListener('input', () => {
        const i = Number(inp.dataset.g);
        const g = Math.max(0, Math.min(5000, Math.round(Number(inp.value) || 0)));
        state.qtys[i].grams = g;
        refreshNumbers();
      });
    });
    body.querySelectorAll<HTMLButtonElement>('[data-minus]').forEach((b) =>
      b.addEventListener('click', () => {
        const i = Number(b.dataset.minus);
        const q = state.qtys[i];
        const stepG = q.unitGrams ?? 10;
        // רצפה: יחידה אחת לפריטים עם יחידה ידועה, 1 ג' לשאר — בלי לרדת ל-0 קלוריות
        if (q.gramsBased) q.grams = Math.max(q.unitGrams ?? 1, Math.round(q.grams - stepG));
        else q.factor = Math.max(0.25, Math.round((q.factor - 0.25) * 100) / 100);
        render();
      }),
    );
    body.querySelectorAll<HTMLButtonElement>('[data-plus]').forEach((b) =>
      b.addEventListener('click', () => {
        const i = Number(b.dataset.plus);
        const q = state.qtys[i];
        const stepG = q.unitGrams ?? 10;
        if (q.gramsBased) q.grams = Math.min(5000, Math.round(q.grams + stepG));
        else q.factor = Math.round((q.factor + 0.25) * 100) / 100;
        render();
      }),
    );
    body.querySelectorAll<HTMLButtonElement>('[data-meal]').forEach((b) =>
      b.addEventListener('click', () => { state.mealType = b.dataset.meal as MealType; render(); }),
    );
    // ברירת המחדל: כל מרכיב בנפרד — כך הם נכנסים ל"הקבועים שלי" כאריחים שאפשר לשלב
    body.querySelector('#fav-toggle')!.addEventListener('click', () => { state.saveFav = state.saveFav ? false : 'items'; render(); });
    body.querySelectorAll<HTMLElement>('[data-favmode]').forEach((b) => b.addEventListener('click', () => {
      state.saveFav = b.dataset.favmode as 'meal' | 'items';
      render();
    }));
    // מחיקת הרישום מתוך העריכה — עם ביטול (מחליף את ה-✕ הקבוע בבית)
    body.querySelector('#confirm-del')?.addEventListener('click', async () => {
      if (busySaving || !state.editId) return;
      busySaving = true;
      const snapshot = editingEntry;
      try {
        await deleteEntry(state.editId);
        pendingRefresh = true;
        closeAddFlow();
        undoToast('הרישום נמחק', () => {
          if (!snapshot) return;
          void restoreEntry(snapshot).then(() => onSavedCb?.()).catch(() => toast('השחזור נכשל', 'error'));
        });
      } catch {
        toast('המחיקה נכשלה — נסי שוב', 'error');
      } finally {
        busySaving = false;
      }
    });
  };

  renderConfirm = render;
  render();
}

async function saveConfirm(state: ConfirmState): Promise<void> {
  if (busySaving) return;
  busySaving = true;
  // משוב מיידי ל-✓: בזמן הניסיונות החוזרים (כמה שניות בקליטה חלשה) הכפתור מנוטרל — הלחיצה "נתפסה".
  // busySaving ממילא בולע לחיצה נוספת, אבל בשקט — ובלי סימן חיצוני זה נראה כמו "לא עבד, אלחץ שוב".
  renderConfirm?.();   // מצייר מחדש עם busySaving=true: ✓ מנוטרל והעריכה חסומה
  const ep = flowEpoch; // אם המשתמשת ניווטה בזמן השמירה (שמתארכת עם הריטריי) — לא סוגרים לה מסך אחר
  const savingJobId = resumedJob?.id;
  const t = stateTotals(state);
  // אותה בנייה כמו בתיקון ל-AI — כולל האייקון הידני, שנשמט כאן (ביקורת 29.09: עריכה/מועדף איבדו אותו)
  const items: EntryItem[] = qtysToItems(state);
  try {
    if (state.editId) {
      // עריכת רישום קיים — נשאר רישום אחד (גם לגסי מרובה-מרכיבים)
      await updateEntry(state.editId, {
        meal_type: state.mealType,
        description: state.description,
        items,
        calories: Math.round(t.calories),
        protein_g: round1(t.protein),
        carbs_g: round1(t.carbs),
        fat_g: round1(t.fat),
        source: state.source,
      });
    } else {
      // מדיניות: כל מרכיב = רישום נפרד ביומן (גם תוצאות AI של צלחת שלמה) — סבב רשת אחד לכולם
      const eatenAt = state.eatenAtIso ?? eatenAtForDay(targetDay);
      await addEntries(items.map((it) => ({
        meal_type: state.mealType,
        description: it.name_he,
        items: [it],
        calories: it.calories ?? 0,
        protein_g: it.protein_g ?? null,
        carbs_g: it.carbs_g ?? null,
        fat_g: it.fat_g ?? null,
        source: state.source,
      })), eatenAt);
    }
    if (state.saveFav === 'items' && items.length > 1) {
      // כל מרכיב כמועדף נפרד (פעם אחת לכל שם) — כמו ⭐ בעמוד המאכל
      const seen = new Set<string>();
      const uniq = items.filter((it) => !seen.has(it.name_he) && !!seen.add(it.name_he));
      await Promise.all(uniq.map((it) => addFavorite({
        name: it.name_he, items: [it], meal_type: state.mealType,
        calories: it.calories ?? 0, protein_g: it.protein_g ?? null,
        carbs_g: it.carbs_g ?? null, fat_g: it.fat_g ?? null,
      }))).catch(() => { /* המועדף לא קריטי — הארוחה כבר נשמרה */ });
    } else if (state.saveFav) {
      // המועדף נשאר "ארוחה שלמה" (תבנית) — בהוספה חוזרת הוא יפוצל לרישומים נפרדים
      await addFavorite({
        name: state.description || items[0]?.name_he || 'ארוחה',
        items, meal_type: state.mealType,
        calories: Math.round(t.calories), protein_g: round1(t.protein),
        carbs_g: round1(t.carbs), fat_g: round1(t.fat),
      }).catch(() => { /* המועדף לא קריטי — הארוחה כבר נשמרה */ });
    }
    // צילום מהתור נשמר — לא מחזירים אותו לתור בסגירה. ואם היא סגרה באמצע השמירה והוא כבר
    // הוחזר לשם — מוציאים אותו, אחרת הכרטיס בבית היה מזמין לאשר (ולשמור) את אותה ארוחה שוב.
    if (savingJobId) {
      if (resumedJob?.id === savingJobId) resumedJob = null;
      photoQueue.take(savingJobId);
    }
    pendingRefresh = true; // הבית ירוענן פעם אחת בסגירה
    // הריטריי מאריך את השמירה לכמה שניות — די זמן לחזור אחורה או לפתוח זרימה חדשה. סוגרים רק אם היא
    // עדיין על מסך האישור הזה (אותו epoch); אם הזרימה כבר נסגרה — מרעננים את הבית מיד (כמו ב-instantAdd);
    // ואם היא באמצע משהו אחר בזרימה — לא שולפים לה את המסך, הבית יתרענן כשתסגור.
    if (flowOpen && ep === flowEpoch) closeAddFlow();
    else if (!flowOpen) { pendingRefresh = false; onSavedCb?.(); }
    toast(state.editId ? 'הארוחה עודכנה ✓' : 'נוסף ליומן ✓');
  } catch (e) {
    const transient = isTransient(e);
    reportError(e, { action: state.editId ? 'update_entry' : 'save_meal', transient });
    const m = e instanceof Error ? e.message : '';
    const msg = m === 'not_found'
      ? 'הארוחה כבר לא קיימת — רוענני'
      // no_session עובר כ"חולף" (רענון סשן שנכשל ברשת שווה ניסיון), אבל כשהוא מגיע עד לכאן
      // הסשן פג והמשתמשת כבר בדרך למסך ההתחברות — "אין חיבור יציב" היה מטעה.
      : m === 'no_session'
        // ⚠️ בלי הבטחה: אין התמדה של שמירה ממתינה, והארוחה **לא** תישמר מעצמה
        ? 'הסשן פג — התחברי מחדש והזיני שוב'
        : transient
          ? 'אין חיבור יציב — נסי שוב עוד רגע'
          : 'השמירה נכשלה — נסי שוב';
    toast(msg, 'error');
  } finally {
    busySaving = false;
    // הצלחה סוגרת את הזרימה; בכשל חוזרים למסך פעיל (הבדיקה מונעת ציור על מסך אחר)
    if (flowOpen && ep === flowEpoch) renderConfirm?.();
  }
}

function qtysToItems(state: ConfirmState): EntryItem[] {
  return state.qtys.map((q) => {
    const v = qtyValues(q);
    return {
      name_he: q.name_he, qty_desc: q.qty_desc,
      grams: q.gramsBased ? Math.round(q.grams) : undefined,
      calories: Math.round(v.calories),
      protein_g: round1(v.protein), carbs_g: round1(v.carbs), fat_g: round1(v.fat),
      unit_grams: q.unitGrams, unit_name: q.unitName,
      icon: q.icon,
    };
  });
}

async function doRefine(state: ConfirmState, correction: string): Promise<void> {
  setPage('הארוחה שלך 🍽️', `<div class="sheet-loading"><div class="sheet-spinner"></div><p>מעדכנת לפי התיקון…</p></div>`);
  const ep = flowEpoch;
  try {
    const { meal } = await refineMeal(qtysToItems(state), correction);
    if (!flowOpen || ep !== flowEpoch) return;
    showConfirm({
      ...toInit(state),
      items: meal.items,
      // הארוחה שנבחרה (לפי השעה או בצ'יפים) גוברת על ניחוש המודל — אותו כלל כמו בניתוח הראשון
      clarify: meal.clarify,
      description: meal.items.map((i) => i.name_he).join(', ') || state.description,
    });
  } catch (e) {
    if (!flowOpen || ep !== flowEpoch) return;
    // עומס/timeout/שאלה מהמודל — אומרים מה קרה, והטקסט שהקלידה מחכה בתיבה לשליחה חוזרת
    reportError(e, { action: 'refine_meal', transient: isTransient(e) });
    showConfirm({ ...toInit(state), refineDraft: correction, refineError: aiErrorMsg(e) });
  }
}

/** מצב המסך → פתיחה מחדש שלו: הכמויות, הארוחה שנבחרה, התמונה, "שמעתי", תמונות שדולגו ו-⭐ */
function toInit(state: ConfirmState): ConfirmInit {
  return {
    items: qtysToItems(state), mealType: state.mealType, clarify: state.clarify,
    skipped: state.skipped, heard: state.heard, photoUrl: state.photoUrl, saveFav: state.saveFav,
    description: state.description, source: state.source, editId: state.editId, eatenAtIso: state.eatenAtIso,
  };
}

// ---------- בורר אייקון ידני (מאכל שלי בלבד) — צמוד לשם, סגור כברירת מחדל ----------

/** כפתור האייקון עצמו (צמוד לתיבת השם) + הפאנל שנפתח מתחתיו כשopen. selected=null → תצוגת
 *  ניחוש אוטומטי לפי nameValue הנוכחי (לא משתנה תוך כדי הקלדה — רק ברינדור הבא). */
function iconPickerParts(selected: string | null, nameValue: string, open: boolean): { btn: string; panel: string } {
  const shown = selected ?? foodEmoji(nameValue || ' ');
  const btn = `<button type="button" class="icon-btn" id="icon-toggle" aria-label="בחירת אייקון">${shown}</button>`;
  if (!open) return { btn, panel: '' };
  const grid = FOOD_ICON_CHOICES
    .map((em) => `<div class="icon-opt${em === selected ? ' sel' : ''}" data-icon="${em}">${em}</div>`)
    .join('');
  const panel = `
    <div class="icon-panel">
      <button type="button" class="icon-auto-btn${selected === null ? ' on' : ''}" id="icon-auto"><span class="ic">🔄</span> אוטומטי לפי שם</button>
      <div class="icon-grid">${grid}</div>
    </div>`;
  return { btn, panel };
}

/** מחווט את הכפתור/פאנל שהוזרקו ע"י iconPickerParts. onChange מקבל את הבחירה החדשה (null=אוטומטי)
 *  ואת האם הפאנל צריך להישאר פתוח — הקריאה לפונקציה הזו קורית אחרי כל render(). */
function wireIconPicker(container: HTMLElement, onToggleOpen: () => void, onSelect: (icon: string | null) => void): void {
  container.querySelector('#icon-toggle')?.addEventListener('click', onToggleOpen);
  container.querySelector('#icon-auto')?.addEventListener('click', () => onSelect(null));
  container.querySelectorAll<HTMLElement>('[data-icon]').forEach((el) =>
    el.addEventListener('click', () => onSelect(el.dataset.icon!)),
  );
}

// ============================================================
// 5. מאכל אישי (מאגר פרטי)
// ============================================================

function showCustomFoodForm(prefillName = ''): void {
  // מצב הטופס חי מחוץ לרינדור — מעבר "ל-100 ג'/למנה" ומילוי-מהתווית לא מאבדים מה שהוקלד.
  const st = {
    mode: '100g' as '100g' | 'portion',
    name: prefillName,
    cal: '', p: '', c: '', f: '',
    servingG: '',            // גודל מנה בגרמים (מצב "למנה")
    uname: '', ugrams: '',
    aiFilled: false,         // הטופס מולא מצילום תווית
    icon: null as string | null, // null = אוטומטי לפי שם
    iconOpen: false,
  };

  /** קריאת הערכים מהשדות אל המצב — לפני כל רינדור מחדש (החלפת מצב) או שמירה */
  const pull = () => {
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
    if (!document.getElementById('cf-name')) return;
    st.name = v('cf-name');
    st.cal = v('cf-cal'); st.p = v('cf-p'); st.c = v('cf-c'); st.f = v('cf-f');
    st.uname = v('cf-uname');
    if (st.mode === 'portion') st.servingG = v('cf-serv');
    else st.ugrams = v('cf-ugrams');
  };

  const showErr = (m: string) => { const el = document.getElementById('sheet-err'); if (el) el.textContent = m; };

  // ---- צילום תווית, עם ניסיון חוזר אוטומטי (29.09.2026) ----
  // ⚠️ עד היום עומס בגוגל = ספינר שמחליף את הטופס, ואז הודעת שגיאה וצילום מחדש. עכשיו הטופס
  // נשאר גלוי (אפשר להקליד בינתיים), ובמקום כפתור הצילום מוצג מה קורה; כשל זמני מנוסה שוב
  // לבד באותם מרווחים של תור הצילומים. לא תור מתמיד כמו בצילום ארוחה — התווית ממלאת טופס
  // פתוח, ואין לה משמעות אחרי שיצאה ממנו.
  const formEp = flowEpoch;
  const onForm = () => flowOpen && flowEpoch === formEp;
  let scan: { base64: string; mime: string; attempts: number; status: 'reading' | 'waiting'; lastError?: string } | null = null;
  let scanTimer: number | undefined;

  const stopScan = () => { window.clearTimeout(scanTimer); scan = null; renderScan(); };

  /** האזור של כפתור הצילום — מתעדכן לבד, בלי לרנדר את הטופס (שלא ייקטע לה הקלדה/מקלדת) */
  const renderScan = () => {
    const box = document.getElementById('cf-scan-box');
    if (!box) return;
    if (!scan) {
      box.innerHTML = `
        <button class="cf-photo-cta" id="cf-photo-btn">
          <span class="ic">📷</span>
          <span class="tx"><span class="t">צילום תווית</span><span class="s">צלמי את טבלת הערכים התזונתיים — אני אמלא את הכל לבד</span></span>
        </button>`;
      box.querySelector('#cf-photo-btn')!.addEventListener('click', () => {
        (document.getElementById('cf-photo-input') as HTMLInputElement).click();
      });
      return;
    }
    const reading = scan.status === 'reading';
    box.innerHTML = `
      <div class="cf-scan">
        ${reading ? '<div class="sheet-spinner"></div>' : '<div class="pw-ico">⏳</div>'}
        ${scan.attempts === 0 ? '<p>קוראת את התווית… ✨</p>' : `
          <b>${escapeHtml(jobReason(scan.lastError))}</b>
          <p>${reading ? 'מנסה שוב עכשיו…' : 'אני ממשיכה לנסות לקרוא את התווית לבד.'} בינתיים אפשר גם להקליד בעצמך.</p>`}
        <div class="cf-scan-actions">
          ${scan.attempts > 0 && !reading ? '<button class="btn-link" id="cf-scan-now">לנסות עכשיו</button>' : ''}
          <button class="btn-link" id="cf-scan-stop">ביטול</button>
        </div>
      </div>`;
    box.querySelector('#cf-scan-now')?.addEventListener('click', () => { window.clearTimeout(scanTimer); void attemptScan(); });
    box.querySelector('#cf-scan-stop')!.addEventListener('click', stopScan);
  };

  /** ערכי התווית נכנסים לטופס — אבל לא דורסים מה שהיא הקלידה בזמן ההמתנה */
  const applyLabel = (label: FoodLabel) => {
    pull();
    const typedNums = [st.cal, st.p, st.c, st.f].some((v) => v.trim());
    if (!typedNums) {
      // ערכים מתווית הם סט אחד (ל-100 ג') — לא מערבבים עם ערך שהקלידה
      st.mode = '100g';
      st.cal = String(label.calories_100g);
      st.p = label.protein_100g != null ? String(label.protein_100g) : '';
      st.c = label.carbs_100g != null ? String(label.carbs_100g) : '';
      st.f = label.fat_100g != null ? String(label.fat_100g) : '';
      st.aiFilled = true;
    }
    // השם מהתווית גובר על שם שהגיע מהחיפוש (prefill), לא על שם שהקלידה
    if (label.name_he && (!st.name.trim() || st.name === prefillName)) st.name = label.name_he;
    if (label.unit_name && label.unit_grams && !st.uname.trim() && !st.ugrams.trim()) {
      st.uname = label.unit_name;
      st.ugrams = String(label.unit_grams);
    }
    render();
    if (typedNums) toast('התווית נקראה — השארתי את הערכים שהקלדת');
    else if (label.clarify) showErr(`🤔 ${label.clarify}`);
  };

  const attemptScan = async (): Promise<void> => {
    const cur = scan;
    if (!cur || !onForm()) { window.clearTimeout(scanTimer); return; }
    cur.status = 'reading';
    renderScan();
    try {
      const label = await parseFoodLabel(cur.base64, cur.mime);
      if (scan !== cur || !onForm()) return;   // בוטל / יצאה מהטופס בינתיים
      scan = null;
      applyLabel(label);
    } catch (e) {
      if (scan !== cur || !onForm()) return;
      const c = classifyError(e);
      if (c.kind === 'clarify') {
        stopScan();
        showErr(`🤔 ${c.clarify}`);
        return;
      }
      cur.attempts++;
      cur.lastError = c.code;
      if (c.kind === 'fatal' || cur.attempts >= MAX_AUTO_ATTEMPTS) {
        stopScan();
        toast(aiErrorMsg(e), 'error');
        return;
      }
      cur.status = 'waiting';
      renderScan();
      scanTimer = window.setTimeout(() => void attemptScan(), retryDelay(cur.attempts));
    }
  };

  /** צילום תווית → ה-AI מחזיר ערכים ל-100 ג' + יחידה → מילוי הטופס */
  const scanLabel = async (file: File) => {
    window.clearTimeout(scanTimer);
    scan = null;
    let img: { base64: string; mime: string };
    try {
      img = await compressImage(file);
      if (!img.base64) throw new Error('bad_image');
    } catch {
      if (onForm()) toast('לא הצלחתי לטעון את התמונה — נסי לצלם שוב', 'error');
      return;
    }
    if (!onForm()) return;
    scan = { ...img, attempts: 0, status: 'reading' };
    await attemptScan();
  };

  const save = async () => {
    pull();
    const num = (s: string) => { const t = s.trim(); if (!t) return null; const n = Number(t); return Number.isFinite(n) ? n : null; };
    const name = st.name.trim();
    if (name.length < 2) return showErr('צריך שם למאכל');
    let cal100: number;
    let p100: number | null, c100: number | null, f100: number | null;
    let portions: FoodPortion[];
    if (st.mode === 'portion') {
      // הזנה כמו על התווית: ערכים למנה + משקל המנה → נרמול ל-100 ג' + היחידה נשמרת
      const g = num(st.servingG);
      const cal = num(st.cal);
      if (!g || g <= 0) return showErr('הזיני את גודל המנה בגרמים');
      if (!cal || cal <= 0) return showErr('הזיני קלוריות למנה');
      const to100 = (v: number | null) => (v === null ? null : round1((v / g) * 100));
      cal100 = Math.round((cal / g) * 100);
      p100 = to100(num(st.p)); c100 = to100(num(st.c)); f100 = to100(num(st.f));
      portions = [{ name_he: st.uname.trim() || 'מנה', grams: g }];
    } else {
      const cal = num(st.cal);
      if (!cal || cal <= 0) return showErr('הזיני קלוריות ל-100 גרם');
      cal100 = cal; p100 = num(st.p); c100 = num(st.c); f100 = num(st.f);
      const uName = st.uname.trim();
      const uGrams = num(st.ugrams);
      portions = uName && uGrams && uGrams > 0 ? [{ name_he: uName, grams: uGrams }] : [];
    }
    if (busySaving) return;
    busySaving = true;
    try {
      await addCustomFood({ name_he: name, calories: cal100, protein_g: p100, carbs_g: c100, fat_g: f100, portions, icon: st.icon });
      toast('המאכל נשמר למאגר ✓');
      replaceWith(showSearch); // מיד אפשר לחפש ולהוסיף אותו
    } catch {
      showErr('השמירה נכשלה — נסי שוב');
    } finally {
      busySaving = false;
    }
  };

  const render = () => {
    const ai = st.aiFilled ? ' cf-ai' : '';
    const segBtn = (m: '100g' | 'portion', label: string) =>
      `<button class="cf-seg-btn${st.mode === m ? ' on' : ''}" data-cfmode="${m}">${label}</button>`;
    const fields = st.mode === '100g'
      ? `
      <div class="cf-grid">
        <label class="cf-label">קלוריות (ל-100 ג')<input class="sheet-input${ai}" id="cf-cal" type="number" inputmode="decimal" min="0" value="${st.cal}" placeholder="250"></label>
        <label class="cf-label">חלבון (ג')<input class="sheet-input${ai}" id="cf-p" type="number" inputmode="decimal" min="0" value="${st.p}" placeholder="8"></label>
        <label class="cf-label">פחמימות (ג')<input class="sheet-input${ai}" id="cf-c" type="number" inputmode="decimal" min="0" value="${st.c}" placeholder="30"></label>
        <label class="cf-label">שומן (ג')<input class="sheet-input${ai}" id="cf-f" type="number" inputmode="decimal" min="0" value="${st.f}" placeholder="12"></label>
      </div>
      <div class="cf-unit-hint muted">יש יחידה? (אופציונלי) — כדי להוסיף לפי כמות, למשל "קרקר = 30 ג'"</div>
      <div class="cf-grid">
        <label class="cf-label">שם יחידה<input class="sheet-input${ai}" id="cf-uname" type="text" value="${escapeHtml(st.uname)}" placeholder="קרקר / יחידה / כף"></label>
        <label class="cf-label">משקל יחידה (ג')<input class="sheet-input${ai}" id="cf-ugrams" type="number" inputmode="decimal" min="0" value="${st.ugrams}" placeholder="30"></label>
      </div>`
      : `
      <div class="cf-grid">
        <label class="cf-label">גודל מנה (ג')<input class="sheet-input" id="cf-serv" type="number" inputmode="decimal" min="0" value="${st.servingG}" placeholder="30"></label>
        <label class="cf-label">קלוריות למנה<input class="sheet-input" id="cf-cal" type="number" inputmode="decimal" min="0" value="${st.cal}" placeholder="121"></label>
        <label class="cf-label">חלבון למנה (ג')<input class="sheet-input" id="cf-p" type="number" inputmode="decimal" min="0" value="${st.p}" placeholder="2.5"></label>
        <label class="cf-label">פחמימות למנה (ג')<input class="sheet-input" id="cf-c" type="number" inputmode="decimal" min="0" value="${st.c}" placeholder="20"></label>
        <label class="cf-label">שומן למנה (ג')<input class="sheet-input" id="cf-f" type="number" inputmode="decimal" min="0" value="${st.f}" placeholder="4"></label>
        <label class="cf-label">שם היחידה<input class="sheet-input" id="cf-uname" type="text" value="${escapeHtml(st.uname)}" placeholder="מנה / קרקר / פרוסה"></label>
      </div>
      <div class="cf-unit-hint muted">הערכים יומרו ל-100 ג' אוטומטית, והיחידה תישמר לבורר הכמות</div>`;

    const iconParts = iconPickerParts(st.icon, st.name, st.iconOpen);

    const body = setPage('מאכל שלי ➕', `
      <div id="cf-scan-box"></div>
      <div class="cf-or">או הזנה ידנית</div>
      ${st.aiFilled ? `<div class="cf-ai-chip">✨ מולא מהתווית — בדקי ותקני אם צריך</div>` : ''}
      <div class="cf-seg">${segBtn('100g', 'ל-100 גרם')}${segBtn('portion', 'למנה מהתווית')}</div>
      <div class="name-field">
        <label class="cf-label">שם המאכל</label>
        <div class="name-row">
          ${iconParts.btn}
          <input class="sheet-input${ai}" id="cf-name" type="text" value="${escapeHtml(st.name)}" placeholder="למשל: עוגת האמא שלי">
        </div>
        ${iconParts.panel}
      </div>
      ${fields}
      <button class="btn-primary" id="cf-save">שמירה למאגר</button>
      <div class="sheet-err" id="sheet-err"></div>
      <input type="file" id="cf-photo-input" accept="image/*" hidden>`);

    renderScan();
    body.querySelector('#cf-photo-input')!.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) void scanLabel(file);
    });
    body.querySelectorAll<HTMLButtonElement>('[data-cfmode]').forEach((b) =>
      b.addEventListener('click', () => {
        const m = b.dataset.cfmode as '100g' | 'portion';
        if (st.mode === m) return;
        pull();
        st.mode = m;
        render();
      }),
    );
    wireIconPicker(body,
      () => { pull(); st.iconOpen = !st.iconOpen; render(); },
      (icon) => { pull(); st.icon = icon; st.iconOpen = false; render(); },
    );
    body.querySelector('#cf-save')!.addEventListener('click', () => void save());
  };

  render();
}

// ============================================================
// 6. קלוריות מהירות
// ============================================================

function showQuickCal(): void {
  let mealType = forcedMeal ?? guessMealType();
  const mealBtns = () => (Object.keys(MEAL_LABEL) as MealType[])
    .map((m) => `<button class="chip${m === mealType ? ' on' : ''}" data-qcmeal="${m}">${MEAL_LABEL[m]}</button>`)
    .join('');
  const render = () => {
    const body = setPage('קלוריות מהירות 🔢', `
      <p class="cf-hint">רישום מהיר של קלוריות (ומקרו אם ידוע) בלי לבחור מאכל.</p>
      <label class="cf-label cf-cal-big">קלוריות<input class="sheet-input" id="qc-cal" type="number" inputmode="numeric" min="0" placeholder="300"></label>
      <div class="cf-grid">
        <label class="cf-label">חלבון (ג')<input class="sheet-input" id="qc-p" type="number" inputmode="numeric" min="0"></label>
        <label class="cf-label">פחמימות (ג')<input class="sheet-input" id="qc-c" type="number" inputmode="numeric" min="0"></label>
        <label class="cf-label">שומן (ג')<input class="sheet-input" id="qc-f" type="number" inputmode="numeric" min="0"></label>
      </div>
      <div class="mt-label">סוג ארוחה</div>
      <div class="meal-type-row">${mealBtns()}</div>
      <div class="sheet-err" id="sheet-err"></div>`);
    const tools = document.getElementById('page-head-tools')!;
    tools.innerHTML = '<button class="ha go" id="qc-save" aria-label="הוספה ליומן">✓</button>';
    // בלי render() — הוא בונה את הטופס מחדש ומוחק את מה שהוקלד (ביקורת 29.09: הקלידה קלוריות,
    // בחרה ארוחה, והמספר נעלם). מחליפים רק את הסימון.
    body.querySelectorAll<HTMLButtonElement>('[data-qcmeal]').forEach((b) =>
      b.addEventListener('click', () => {
        mealType = b.dataset.qcmeal as MealType;
        body.querySelectorAll('[data-qcmeal]').forEach((x) => x.classList.toggle('on', x === b));
      }),
    );
    (body.querySelector('#qc-cal') as HTMLInputElement).focus();
    tools.querySelector('#qc-save')!.addEventListener('click', async () => {
      const num = (id: string) => { const v = (document.getElementById(id) as HTMLInputElement).value; return v ? Number(v) : null; };
      const cal = num('qc-cal');
      if (cal === null || !Number.isFinite(cal) || cal <= 0) {
        return void (document.getElementById('sheet-err')!.textContent = 'הזיני מספר קלוריות');
      }
      if (busySaving) return;
      busySaving = true;
      try {
        await addEntry({
          meal_type: mealType, description: 'רישום מהיר', items: [],
          calories: Math.round(cal), protein_g: num('qc-p'), carbs_g: num('qc-c'), fat_g: num('qc-f'),
          source: 'manual',
        }, eatenAtForDay(targetDay));
        pendingRefresh = true;
        closeAddFlow();
        toast('נוסף ליומן ✓');
      } catch (e) {
        const transient = isTransient(e);
        reportError(e, { action: 'quick_cal', transient });
        // ⚠️ `!` על אלמנט שהוסר (הזרימה נסגרה בזמן השמירה) זורק *בתוך ה-catch* — דחייה
        // לא מטופלת ואפס משוב. טוסט שורד הסרת מסך; ה-err הוא תוספת כשהוא קיים.
        const m = e instanceof Error ? e.message : '';
        const msg = m === 'no_session' ? 'הסשן פג — התחברי מחדש'
          : transient ? 'אין חיבור יציב — נסי שוב עוד רגע' : 'השמירה נכשלה — נסי שוב';
        const errEl = document.getElementById('sheet-err');
        if (errEl) errEl.textContent = msg; else toast(msg, 'error');
      } finally { busySaving = false; }
    });
  };
  render();
}

// ============================================================
// 7. חיפוש והוספה v4 — הוספה מיידית (✓), חלונית כמות בשורה, בחירת פריטים מארוחה
// ============================================================

type SearchTab = 'all' | 'fav' | 'recent' | 'mine';
let searchTab: SearchTab = 'all';
let foodStats: FoodStats | null = null; // "כמות מהפעם הקודמת" + תדירים. מתאפס אחרי שמירה ואחרי 30 דק'
let foodStatsAt = 0;
const FOOD_STATS_TTL = 30 * 60 * 1000;

/** איפוס הקאשים של זרימת ההוספה — ביציאה מהחשבון (main.ts), כדי שמשתמשת אחרת באותה לשונית לא
 *  תראה את התדירים / האחרונות / החיפוש של הקודמת (ביקורת 29.09). */
export function invalidateAddFlowCache(): void {
  foodStats = null;
  foodStatsAt = 0;
  defaultRecentCache = null;
  lastQuery = '';
}
// תוצאת fetchRecentGrouped(4) של מסך הפתיחה (טאב "הכל", בלי שאילתה) — נשלפת פעם אחת בפתיחת
// החיפוש ולא שוב כשה-render השני (אחרי שסטטיסטיקות המאכלים חוזרות) מוסיף את סעיף "תדירים".
let defaultRecentCache: Entry[] | null = null;
let renderSeq = 0;                      // מונע מרוץ בין החלפת טאב לתשובת רשת איטית
/** השאילתה האחרונה — שורדת מעבר לעמוד מאכל וחזרה; מתנקה אחרי הוספה. */
let lastQuery = '';

/** ניקוי החיפוש אחרי הוספה מוצלחת (נשארים בחיפוש, מוכנים לבא) */
function clearSearchQuery(): void {
  lastQuery = '';
  const input = document.getElementById('food-q') as HTMLInputElement | null;
  if (input) {
    input.value = '';
    void renderSearchContent('');
  }
}

/** שורת מאכל בודד: מוסיפים כמות אחת ליומן */
interface RowFood {
  kind: 'food';
  name: string;
  custom?: boolean;
  /** ערכים לגרם (אם ידוע משקל) — לחישוב חי בעמוד המאכל */
  perGram: { cal: number; p: number; c: number; f: number } | null;
  /** הפריט בברירת המחדל (הכמות מהפעם הקודמת, או מנה/100 ג') */
  base: EntryItem;
  /** יחידות לקומבו: גרם + מנות בעלות שם */
  units: Array<{ label: string; grams: number }>;
  defMeal: MealType;
  sub: string;
  /** המאכל המקורי מהמאגר/האישי — לעריכה (✎). שורות היסטוריה-בלבד בלי זה. */
  food?: Food;
  /** ברירת המחדל מגיעה מהפעם הקודמת (להעדפה בדירוג התוצאות) */
  hist?: boolean;
  /** קיים כשהשורה עצמה היא מועדף (נפתחה מטאב "מועדפים") — מזהה את רשומת המועדף להסרה */
  favId?: string;
}

/** שורת ארוחה שלמה (אחרונה/מועדפת): ✓ מוסיף את כולה, נגיעה = בחירת פריטים */
interface RowMeal {
  kind: 'meal';
  entry: Entry;
  label: string;
}

type Row = RowFood | RowMeal;
let rowCache: Row[] = [];

/** "היום"/"אתמול"/"שלשום"/תאריך קצר */
function relDay(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  if (diff <= 0) return 'היום';
  if (diff === 1) return 'אתמול';
  if (diff === 2) return 'שלשום';
  return new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'numeric' }).format(d);
}

/** יחידות לקומבו מתוך portions של מאכל (גרם תמיד ראשון; מנות "גרם" של המאגר מסוננות — כפולות) */
function foodUnits(portions: Array<{ name_he: string; grams: number }>): Array<{ label: string; grams: number }> {
  const named = portions
    .filter((p) => p.grams > 1 && p.name_he !== '100 גרם' && p.name_he !== 'גרם')
    .slice(0, 5)
    .map((p) => ({ label: p.name_he, grams: p.grams }));
  return [{ label: 'גרם', grams: 1 }, ...named];
}

/** תיאור כמות עם גרמים: "4 × יחידה (120 ג')" / "150 ג'" / "מנה" */
function qtyWithGrams(it: EntryItem): string {
  if (it.qty_desc && it.grams) {
    return it.qty_desc.includes('ג\'') ? it.qty_desc : `${it.qty_desc} (${fmt(it.grams)} ג')`;
  }
  if (it.grams) return `${fmt(it.grams)} ג'`;
  return it.qty_desc || 'מנה';
}

/** מאכל מהמאגר/אישי → שורה. אם נאכל בעבר — ברירת המחדל היא הכמות מהפעם הקודמת. */
function foodToRow(f: Food): RowFood {
  const perGram = { cal: f.calories / 100, p: (f.protein_g ?? 0) / 100, c: (f.carbs_g ?? 0) / 100, f: (f.fat_g ?? 0) / 100 };
  const hist = foodStats?.byName.get(f.name_he);
  if (hist && hist.item.grams && hist.item.grams > 0) {
    return {
      kind: 'food', name: f.name_he, custom: f.custom, perGram, food: f, hist: true,
      base: { ...hist.item, icon: f.icon ?? undefined }, // האייקון הנוכחי של המאכל, לא תמונת-מצב ישנה מההיסטוריה
      units: foodUnits(f.portions),
      defMeal: forcedMeal ?? guessMealType(),
      sub: `${qtyWithGrams(hist.item)} · בפעם הקודמת`,
    };
  }
  // ברירת המחדל, לפי סדר:
  //  1. **מאכל אישי — תמיד היחידה שהמשתמשת הגדירה.** היא טרחה להזין "קרקר אחד = 6 ג'",
  //     וזו בדיוק הכוונה שלה. (נשרפנו 21.09.2026: אנסטסיה הגדירה יחידה של 6 ג', המסך
  //     נפתח על 100 גרם והתעלם ממנה, והיא נאלצה לחשב 3 קרקרים = 18 ג' בעצמה.)
  //  2. מנה במאגר הלאומי בטווח 25–500 ג', הקרובה למנה טיפוסית ~150 ג' — ההגנה מפני
  //     "כפית" שנבחרת כשקיימת מנה אמיתית.
  //  3. אין כזו, אבל יש **יחידה שמית יחידה** — היא היחידה הטבעית של המאכל, בכל גודל.
  //     1,843 מאכלים במאגר הם כאלה (תמר 12 ג', פרוסת פסטרמה 20 ג', כוס פופקורן 8 ג'),
  //     ועבורם 100 גרם היא ברירת מחדל אבסורדית.
  const named = f.portions.filter((p) => p.grams > 1 && p.name_he !== '100 גרם' && p.name_he !== 'גרם');
  const inRange = named.filter((p) => p.grams >= 25 && p.grams <= 500);
  const p0 = f.custom && named.length
    ? named[0]
    : inRange.length
      ? inRange.reduce((a, b) => (Math.abs(a.grams - 150) <= Math.abs(b.grams - 150) ? a : b))
      : named.length === 1
        ? named[0]
        : undefined;
  const g = p0?.grams ?? 100;
  const base: EntryItem = {
    name_he: f.name_he,
    qty_desc: p0?.name_he ?? '100 גרם',
    grams: g,
    calories: Math.round(perGram.cal * g),
    protein_g: round1(perGram.p * g),
    carbs_g: round1(perGram.c * g),
    fat_g: round1(perGram.f * g),
    unit_grams: p0?.grams,
    unit_name: p0?.name_he,
    icon: f.icon ?? undefined,
  };
  return {
    kind: 'food', name: f.name_he, custom: f.custom, perGram, food: f, base,
    units: foodUnits(f.portions),
    defMeal: forcedMeal ?? guessMealType(),
    // הכתובית מתארת בדיוק את מה שה-✓ יוסיף — אותה מנה שהמספר הגדול מציג
    sub: p0 ? `${p0.name_he} · ${fmt(g)} ג'` : `100 ג'`,
  };
}

/** מאכל מההיסטוריה (תדירים) → שורה עם הכמות מהפעם הקודמת */
function statToRow(s: FoodStat): RowFood {
  const it = s.item;
  const perGram = it.grams && it.grams > 0
    ? { cal: (it.calories ?? 0) / it.grams, p: (it.protein_g ?? 0) / it.grams, c: (it.carbs_g ?? 0) / it.grams, f: (it.fat_g ?? 0) / it.grams }
    : null;
  return {
    kind: 'food', name: s.name, perGram,
    base: { ...it },
    units: perGram ? [{ label: 'גרם', grams: 1 }] : [{ label: 'מנה', grams: 0 }],
    defMeal: forcedMeal ?? guessMealType(),
    sub: `${qtyWithGrams(it)} · בפעם הקודמת`,
  };
}

/** מועדף עם פריט בודד → שורת מאכל רגילה (עמוד המאכל המלא, כמו מהמאגר/שלי/אחרונות) —
 *  לא צריך רשומת Food מקורית: perGram נגזר ישירות מהתמונת-המצב שנשמרה במועדף. מועדף
 *  מרובה-פריטים (ארוחה שלמה) נשאר בורר-הוספה של ⋯ (RowMeal) — אין "עמוד מאכל" לצירוף שלם. */
function favToRow(f: Favorite): RowFood {
  const it = favItemForLog(f.items[0]);   // ✓ כותב את base ליומן — בלי aliases
  const perGram = it.grams && it.grams > 0
    ? { cal: (it.calories ?? 0) / it.grams, p: (it.protein_g ?? 0) / it.grams, c: (it.carbs_g ?? 0) / it.grams, f: (it.fat_g ?? 0) / it.grams }
    : null;
  const units: Array<{ label: string; grams: number }> = perGram ? [{ label: 'גרם', grams: 1 }] : [{ label: 'מנה', grams: 0 }];
  if (it.unit_name && it.unit_grams && it.unit_name !== 'גרם') units.push({ label: it.unit_name, grams: it.unit_grams });
  return {
    kind: 'food', name: it.name_he, perGram,
    base: { ...it },
    units,
    defMeal: forcedMeal ?? guessMealType(),
    sub: `${qtyWithGrams(it)} · מהמועדפים`,
    favId: f.id,
  };
}

function showSearch(): void {
  // הטאב לא מתאפס כאן — חזרה מעמוד מאכל משאירה את המשתמשת בטאב שבו הייתה
  const tabBtn = (id: SearchTab, label: string) =>
    `<button class="stab${id === searchTab ? ' on' : ''}" data-stab="${id}">${label}</button>`;
  const body = setPage('הוספת מזון', `
    <div class="search-top">
      <div class="search-input-wrap">
        <input class="sheet-input search-input" id="food-q" type="search" placeholder="חיפוש מאכל…">
        <button type="button" class="search-clear" id="food-q-clear" aria-label="ניקוי חיפוש" hidden>✕</button>
      </div>
      <div class="search-acts">
        <button class="s-act primary" data-sact="photo"><div class="ai">📷</div><div class="al">צילום</div></button>
        <button class="s-act" data-sact="regulars"><div class="ai">⚡</div><div class="al">קבועים</div></button>
        <button class="s-act" data-sact="text"><div class="ai">✏️</div><div class="al">תיאור</div></button>
        <button class="s-act" data-sact="custom"><div class="ai">➕</div><div class="al">מאכל שלי</div></button>
        <button class="s-act" data-sact="quick"><div class="ai">🔢</div><div class="al">מהיר</div></button>
      </div>
      <div class="stabs">
        ${tabBtn('all', 'הכל')}${tabBtn('fav', 'מועדפים')}${tabBtn('recent', 'אחרונות')}${tabBtn('mine', 'שלי')}
      </div>
    </div>
    <div id="food-results" class="food-results"></div>
    <input type="file" id="photo-input-s" accept="image/*" multiple hidden>`);

  // כלי כותרת: מונה הוספות + כפתור סיום (סוגר וחוזר לבית המרוענן)
  const tools = document.getElementById('page-head-tools')!;
  tools.innerHTML = `
    <span class="sess-badge" id="sess-count"></span>
    <button class="head-done" id="search-done" aria-label="סיום">✓</button>`;
  tools.querySelector('#search-done')!.addEventListener('click', () => closeAddFlow());
  updateSessionCounter();

  // בכוונה בלי focus() — שהמקלדת לא תקפוץ ותסתיר את ההצעות (מי שרוצה להקליד נוגע בשדה)
  // שחזור השאילתה האחרונה (חזרה מעמוד מאכל לא מאפסת את החיפוש)
  const input = body.querySelector<HTMLInputElement>('#food-q')!;
  const clearBtn = body.querySelector<HTMLButtonElement>('#food-q-clear')!;
  input.value = lastQuery;
  clearBtn.hidden = !lastQuery;
  clearBtn.addEventListener('click', () => {
    input.value = '';
    lastQuery = '';
    clearBtn.hidden = true;
    input.focus();
    void renderSearchContent('');
  });

  body.querySelectorAll<HTMLButtonElement>('[data-stab]').forEach((b) =>
    b.addEventListener('click', () => {
      searchTab = b.dataset.stab as SearchTab;
      body.querySelectorAll('.stab').forEach((t) => t.classList.toggle('on', t === b));
      void renderSearchContent(input.value);
    }),
  );
  body.querySelector('[data-sact="photo"]')!.addEventListener('click', () => {
    (document.getElementById('photo-input-s') as HTMLInputElement).click();
  });
  body.querySelector('#photo-input-s')!.addEventListener('change', (e) => {
    const files = (e.target as HTMLInputElement).files;
    if (files?.length) void handlePhoto(files);
  });
  // הקבועים (המועדפים כאריחים) — אותו מסך כמו מתפריט ה+; חזרה מחזירה לחיפוש (בר, 30.09.2026)
  body.querySelector('[data-sact="regulars"]')!.addEventListener('click', () => navTo(showRegulars));
  // תיאור בטקסט — אותו מסך כמו מתפריט ה+; מה שהוקלד בחיפוש עובר כנקודת פתיחה
  body.querySelector('[data-sact="text"]')!.addEventListener('click', () => {
    const q = (document.getElementById('food-q') as HTMLInputElement | null)?.value.trim() ?? '';
    navTo(() => showTextInput(q));
  });
  body.querySelector('[data-sact="custom"]')!.addEventListener('click', () => {
    const q = (document.getElementById('food-q') as HTMLInputElement | null)?.value.trim() ?? '';
    navTo(() => showCustomFoodForm(q));
  });
  body.querySelector('[data-sact="quick"]')!.addEventListener('click', () => navTo(showQuickCal));

  let timer: number | undefined;
  input.addEventListener('input', () => {
    lastQuery = input.value;
    clearBtn.hidden = !input.value;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void renderSearchContent(input.value), 250);
  });

  // סטטיסטיקת מאכלים ברקע ואז רינדור (הרינדור הראשון רץ מיד עם מה שיש). קאש לכל הסשן —
  // בפתיחות חוזרות של החיפוש אין צורך לשלוף שוב. "אחרונות" של מסך הפתיחה מתאפסת כל פתיחה.
  defaultRecentCache = null;
  void renderSearchContent(lastQuery);
  if (foodStats === null || Date.now() - foodStatsAt > FOOD_STATS_TTL) {
    void fetchFoodStats().then((s) => {
      foodStats = s;
      foodStatsAt = Date.now();
      const q = (document.getElementById('food-q') as HTMLInputElement | null)?.value ?? '';
      void renderSearchContent(q);
    }).catch(() => {});
  }
}

/** רינדור תוכן החיפוש לפי הטאב הפעיל + השאילתה */
async function renderSearchContent(q: string): Promise<void> {
  const box = document.getElementById('food-results');
  if (!box) return;
  const query = q.trim();
  const seq = ++renderSeq;
  const stale = () => seq !== renderSeq || !document.getElementById('food-results');

  if (searchTab === 'all' && query.length < 3) {
    // מצב פתיחה: תדירים בשעה הזו → ארוחות אחרונות (מקובצות יום+סוג) → יצירת מאכל חדש
    const recent = defaultRecentCache ?? await fetchRecentGrouped(4).catch(() => [] as Entry[]);
    defaultRecentCache = recent;
    if (stale()) return;
    rowCache = [];
    let html = '';
    const freq = foodStats ? frequentAt(foodStats.byName.values(), new Date().getHours()) : [];
    if (freq.length) {
      html += `<div class="fav-head">🕐 תדירים בשעה הזו</div>`;
      for (const s of freq) { rowCache.push(statToRow(s)); html += rowHtml(rowCache.length - 1); }
    }
    if (recent.length) {
      html += `<div class="fav-head">🍽️ ארוחות אחרונות</div>`;
      for (const e of recent) {
        rowCache.push({ kind: 'meal', entry: e, label: `${MEAL_LABEL[(e.meal_type ?? 'snack') as MealType]} · ${relDay(e.eaten_at)}` });
        html += rowHtml(rowCache.length - 1);
      }
    }
    html += `<button class="newrow" id="new-food-row"><span class="nic">➕</span>יצירת מאכל חדש</button>`;
    if (!freq.length && !recent.length) {
      html = `<div class="muted" style="padding:8px 2px">התחילי להקליד כדי לחפש במאגר — או צרי מאכל חדש:</div>` + html;
    }
    box.innerHTML = html;
    wireRows(box);
    return;
  }

  if (searchTab === 'all') {
    box.innerHTML = `<div class="muted" style="padding:8px 2px">מחפשת…</div>`;
    try {
      const { foods, fuzzyOnly } = await searchFoods(query);
      if (stale()) return;
      // דה-דופ לפי שם (מאכל שנאכל לא מופיע פעמיים) + מה שאכלת בעבר קודם
      const seenNames = new Set<string>();
      rowCache = foods.map(foodToRow)
        .filter((r) => { if (seenNames.has(r.name)) return false; seenNames.add(r.name); return true; })
        .sort((a, b) => Number(!!(b as RowFood).hist) - Number(!!(a as RowFood).hist));
      const fuzzyNote = fuzzyOnly && rowCache.length
        ? `<div class="fuzzy-note">לא נמצאה התאמה מדויקת — מציגה תוצאות דומות</div>`
        : '';
      box.innerHTML = fuzzyNote + (rowCache.map((_, i) => rowHtml(i)).join('') ||
        `<div class="muted" style="padding:8px 2px">לא נמצא — אפשר ליצור מאכל חדש:</div>`) +
        `<button class="newrow" id="new-food-row"><span class="nic">➕</span>יצירת מאכל חדש</button>`;
      wireRows(box);
    } catch {
      if (stale()) return;
      box.innerHTML = `<div class="muted" style="padding:8px 2px">החיפוש נכשל — בדקי חיבור</div>`;
    }
    return;
  }

  // מועדפים / אחרונות / שלי
  box.innerHTML = `<div class="muted" style="padding:8px 2px">טוען…</div>`;
  try {
    if (searchTab === 'fav') {
      const favs = await fetchFavorites().catch(() => [] as Favorite[]);
      if (stale()) return;
      // אותו סדר אוטומטי כמו הקבועים — ארוחה, שעה ותדירות (foodStats נטען ברקע; עד שהוא חוזר — סדר השמירה)
      const ordered = foodStats
        ? orderFavorites(favs, [...foodStats.byName.values()], new Date().getHours(), forcedMeal ?? guessMealType())
        : favs;
      const list = ordered.filter((f) => !query || f.name.includes(query));
      // מועדף עם פריט בודד = עמוד מאכל מלא (כמו מהמאגר/שלי/אחרונות); ארוחה שלמה = בורר-הוספה
      rowCache = list.map((f) => f.items.length === 1
        ? favToRow(f)
        : { kind: 'meal' as const, entry: favToEntry(f), label: f.name ?? 'מועדף' });
      box.innerHTML = rowCache.map((_, i) => rowHtml(i)).join('') ||
        `<div class="muted" style="padding:8px 2px">אין מועדפים עדיין — סמני ⭐ בשמירת ארוחה</div>`;
      wireRows(box);
    } else if (searchTab === 'recent') {
      const recent = await fetchRecentGrouped(12).catch(() => [] as Entry[]);
      if (stale()) return;
      const list = recent.filter((m) => !query || (m.description ?? '').includes(query) || (m.items ?? []).some((it) => it.name_he.includes(query)));
      rowCache = list.map((e) => ({ kind: 'meal' as const, entry: e, label: `${MEAL_LABEL[(e.meal_type ?? 'snack') as MealType]} · ${relDay(e.eaten_at)}` }));
      box.innerHTML = rowCache.map((_, i) => rowHtml(i)).join('') ||
        `<div class="muted" style="padding:8px 2px">אין ארוחות אחרונות</div>`;
      wireRows(box);
    } else {
      const mine = await listCustomFoods().catch(() => [] as Food[]);
      if (stale()) return;
      const filtered = query ? mine.filter((f) => f.name_he.includes(query)) : mine;
      rowCache = filtered.map(foodToRow);
      box.innerHTML = (rowCache.map((_, i) => rowHtml(i)).join('') ||
        `<div class="muted" style="padding:8px 2px">אין עדיין מאכלים משלך</div>`) +
        `<button class="newrow" id="new-food-row"><span class="nic">➕</span>יצירת מאכל חדש</button>`;
      wireRows(box);
    }
  } catch {
    if (stale()) return;
    box.innerHTML = `<div class="muted" style="padding:8px 2px">משהו השתבש — נסי שוב</div>`;
  }
}

// ---------- רינדור שורות + חיווט ----------

function rowHtml(idx: number): string {
  const r = rowCache[idx];
  if (r.kind === 'food') {
    const icon = r.food?.icon || r.base.icon || foodEmoji(r.name);
    return `<div class="food-row" data-row="${idx}">
      <span class="fr-em">${icon}</span>
      <div class="fr-mid"><span class="fn">${escapeHtml(r.name)}${r.custom ? '<span class="food-badge">שלי</span>' : ''}</span>
      <span class="fk">${escapeHtml(r.sub)}</span></div>
      <span class="row-kc">${fmt(r.base.calories ?? 0)}</span>
      <button class="vbtn" data-v="${idx}" aria-label="הוספה מיידית">✓</button>
    </div>`;
  }
  const items = entryToItems(r.entry);
  const names = items.map((i) => i.name_he).join(', ');
  return `<div class="food-row meal-row" data-row="${idx}">
    <span class="fr-em">${foodEmoji(names)}</span>
    <div class="fr-mid"><span class="fn">${escapeHtml(r.label)}</span>
    <span class="items-line">${escapeHtml(names)}</span>
    <span class="fk">${fmt(r.entry.calories)} קק"ל · ${items.length} פריטים</span></div>
    <button class="vbtn" data-v="${idx}" aria-label="הוספת כל הארוחה">✓</button>
  </div>`;
}

function wireRows(box: HTMLElement): void {
  // ✓ — הוספה מיידית (עוצר את אירוע השורה כדי שלא ייפתח עמוד המאכל)
  box.querySelectorAll<HTMLButtonElement>('[data-v]').forEach((btn) =>
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const r = rowCache[Number(btn.dataset.v)];
      if (!r) return;
      const done = (ok: boolean) => { if (ok) clearSearchQuery(); }; // ניקוי רק בהצלחה
      if (r.kind === 'food') {
        void instantAdd([{ ...r.base }], r.defMeal, r.name, r.name).then(done);
      } else {
        const items = entryToItems(r.entry).map((i) => ({ ...i }));
        // הארוחה לפי שעת ההזנה, לא לפי מתי זה נאכל בפעם הקודמת (ביקורת 29.09: ✓ על ארוחת בוקר
        // של אתמול ב-20:00 נחת בשקט בבוקר). כפוי גובר. הטוסט אומר לאן זה נכנס.
        // בטוסט — מה נוסף, לא תווית השורה ("בוקר · אתמול נוסף לנשנוש" מבלבל)
        const what = items.length === 1 ? items[0].name_he : `${items.length} פריטים`;
        void instantAdd(items, forcedMeal ?? guessMealType(), r.entry.description ?? 'ארוחה', what).then(done);
      }
    }),
  );
  // נגיעה על גוף השורה — עמוד מאכל מלא / בחירת פריטים מארוחה
  box.querySelectorAll<HTMLElement>('[data-row]').forEach((rowEl) =>
    rowEl.addEventListener('click', () => {
      const idx = Number(rowEl.dataset.row);
      const r = rowCache[idx];
      if (!r) return;
      if (r.kind === 'food') navTo(() => showFoodPage(r));
      else openMealPicker(r.entry, r.label);
    }),
  );
  box.querySelector('#new-food-row')?.addEventListener('click', () => {
    // מה שהוקלד בחיפוש הופך לשם ההתחלתי של המאכל החדש
    const q = (document.getElementById('food-q') as HTMLInputElement | null)?.value.trim() ?? '';
    navTo(() => showCustomFoodForm(q));
  });
}

function favToEntry(f: Favorite): Entry {
  return {
    id: '', user_id: '', eaten_at: new Date().toISOString(),
    meal_type: f.meal_type, description: f.name, items: f.items.map(favItemForLog),
    calories: f.calories, protein_g: f.protein_g, carbs_g: f.carbs_g, fat_g: f.fat_g,
    source: 'favorite', is_favorite: true,
  };
}

// ---------- עמוד מאכל מלא (נגיעה על שורה) ----------

let adminCache: boolean | null = null;

/** תווית אופציה בקומבו היחידות — עם משקל היחידה */
function unitLabel(u: { label: string; grams: number }): string {
  if (u.grams === 1) return 'גרם';
  if (u.grams === 0) return u.label;
  return `${u.label} · ${fmt(u.grams)} ג'`;
}

function showFoodPage(row: RowFood, edit?: { id: string }): void {
  const factorMode = !row.perGram;
  let unitIdx = 0;
  let qty = factorMode ? 1 : (row.base.grams ?? 100);
  // ברירת מחדל שפויה (פיצ'ר 3): אם למאכל נבחרה יחידה שמית (גביע/פרוסה וכו') —
  // פותחים על "1 × היחידה" ולא על גרם גולמי. רק בהוספה חדשה — בעריכת רישום קיים
  // (edit) נשארים על הגרמים המדויקים שכבר נשמרו, כדי לא לעגל כמות שהוזנה ידנית.
  if (!edit && !factorMode && row.base.unit_grams && row.base.unit_grams > 1) {
    const idx = row.units.findIndex((u) => u.grams === row.base.unit_grams);
    if (idx > 0) { unitIdx = idx; qty = 1; }
  }
  // בסיס לצ'יפי ×0.5–×2: "1×" תמיד חוזר לכמות הזו (מתעדכן גם במעבר יחידה)
  let refQty = qty;
  let mealType: MealType = row.defMeal;
  // כשהשורה עצמה היא מועדף (נפתחה מטאב "מועדפים") — הכוכב כבר מסומן ואפשר להסיר ממנו
  let favId: string | undefined = row.favId;
  let favBusy = false;
  let targets: { proteinG: number; carbsG: number; fatG: number } | null = null;

  const vals = () => {
    if (factorMode || row.units[unitIdx].grams === 0) {
      const f = qty;
      const grams = row.base.grams ? Math.round(row.base.grams * f) : undefined;
      const qtyDesc = f === 1 ? (row.base.qty_desc ?? 'מנה') : `${f} × ${row.base.qty_desc ?? 'מנה'}`;
      return {
        cal: (row.base.calories ?? 0) * f, p: (row.base.protein_g ?? 0) * f,
        c: (row.base.carbs_g ?? 0) * f, fat: (row.base.fat_g ?? 0) * f,
        grams, qtyDesc,
        desc: grams ? `${qtyDesc} = ${fmt(grams)} ג'` : qtyDesc,
      };
    }
    const g = qty * row.units[unitIdx].grams;
    const pg = row.perGram!;
    const isGram = row.units[unitIdx].grams === 1;
    const qtyDesc = isGram ? `${fmt(g)} ג'` : `${qty} × ${row.units[unitIdx].label}`;
    return {
      cal: pg.cal * g, p: pg.p * g, c: pg.c * g, fat: pg.f * g, grams: Math.round(g),
      qtyDesc,
      desc: isGram ? `${fmt(g)} ג'` : `${qty} × ${row.units[unitIdx].label} = ${fmt(g)} ג'`,
    };
  };

  const macroTile = (colorVar: string, label: string, id: string) => `
    <div class="macro">
      <div class="name"><span class="dot" style="background:var(--${colorVar})"></span>${label}</div>
      <div class="val"><span id="${id}"></span> <span>ג'</span></div>
      <div class="meter" style="background:var(--${colorVar}-track)"><i id="${id}-m" style="width:0;background:var(--${colorVar})"></i></div>
    </div>`;
  const mealChips = () => (Object.keys(MEAL_LABEL) as MealType[])
    .map((m) => `<button class="mc${m === mealType ? ' on' : ''}" data-fpm="${m}">${MEAL_LABEL[m]}</button>`)
    .join('');
  const unitOptions = row.units
    .map((u, i) => `<option value="${i}"${i === unitIdx ? ' selected' : ''}>${escapeHtml(unitLabel(u))}</option>`)
    .join('');

  const body = setPage(row.name, `
    <div class="fp-hero">
      <div class="n" id="fp-cal"></div>
      <div class="u">קק"ל</div>
      <div class="g" id="fp-desc"></div>
    </div>
    <div class="macros fp-macros">
      ${macroTile('protein', 'חלבון', 'fp-p')}
      ${macroTile('carbs', 'פחמימות', 'fp-c')}
      ${macroTile('fat', 'שומן', 'fp-f')}
    </div>
    <div class="card fp-card">
      <div class="fp-lbl">כמות</div>
      <div class="qty-row">
        <input class="qty-num" id="fp-qty" type="number" inputmode="decimal" min="0" value="${qty}">
        <select class="unit-sel" id="fp-unit">${unitOptions}</select>
      </div>
      <div class="xchips" id="fp-xchips">
        ${[0.5, 1, 1.5, 2].map((v) => `<button class="xc" data-fpx="${v}">${v === 1 ? 'רגיל' : `×${v}`}</button>`).join('')}
      </div>
    </div>
    <div class="card fp-card">
      <div class="fp-lbl">לאיזו ארוחה?</div>
      <div class="mchips" id="fp-meals">${mealChips()}</div>
    </div>`);

  // כותרת: ☆ מועדף · ✎ עריכה (בעלים/אדמין) · ✓ הוספה ליומן
  const tools = document.getElementById('page-head-tools')!;
  tools.innerHTML = `
    <button class="ha star${favId ? ' on' : ''}" id="fp-fav" aria-label="${favId ? 'הסרה מהמועדפים' : 'שמירה במועדפים'}">${favId ? '★' : '☆'}</button>
    <button class="ha" id="fp-edit" aria-label="עריכת המאכל" hidden>✎</button>
    <button class="ha go" id="fp-add" aria-label="הוספה ליומן">✓</button>`;

  const refresh = () => {
    const v = vals();
    const set = (id: string, txt: string) => { const el = document.getElementById(id); if (el) el.textContent = txt; };
    set('fp-cal', fmt(v.cal));
    set('fp-desc', v.desc);
    set('fp-p', fmt(v.p));
    set('fp-c', fmt(v.c));
    set('fp-f', fmt(v.fat));
    if (targets) {
      const meter = (id: string, val: number, target: number) => {
        const el = document.getElementById(id);
        if (el && target > 0) el.style.width = `${Math.min(100, (val / target) * 100)}%`;
      };
      meter('fp-p-m', v.p, targets.proteinG);
      meter('fp-c-m', v.c, targets.carbsG);
      meter('fp-f-m', v.fat, targets.fatG);
    }
    const meals = document.getElementById('fp-meals');
    if (meals) {
      meals.innerHTML = mealChips();
      meals.querySelectorAll<HTMLButtonElement>('[data-fpm]').forEach((b) =>
        b.addEventListener('click', () => { mealType = b.dataset.fpm as MealType; refresh(); }),
      );
    }
    // צ'יפי ×0.5–×2: מדגישים את הכפולה התואמת לכמות הנוכחית ביחס ל-refQty
    document.querySelectorAll<HTMLButtonElement>('#fp-xchips [data-fpx]').forEach((b) => {
      const target = chipTarget(Number(b.dataset.fpx));
      b.classList.toggle('on', Math.abs(qty - target) < 0.01);
    });
  };
  const chipTarget = (v: number): number => (
    !factorMode && row.units[unitIdx].grams === 1 ? Math.round(refQty * v) : round1(refQty * v)
  );
  refresh();
  // מדי המאקרו יחסית ליעד היומי (מהקאש; אם אין — נשארים ריקים)
  void fetchTargets().then((t) => { if (t) { targets = t; refresh(); } }).catch(() => {});

  const qtyInput = body.querySelector('#fp-qty') as HTMLInputElement;
  qtyInput.addEventListener('focus', () => qtyInput.select());
  qtyInput.addEventListener('input', () => {
    const n = Number(qtyInput.value);
    qty = Number.isFinite(n) && n > 0 ? n : 0;
    refresh();
  });
  body.querySelectorAll<HTMLButtonElement>('#fp-xchips [data-fpx]').forEach((b) =>
    b.addEventListener('click', () => {
      qty = chipTarget(Number(b.dataset.fpx));
      qtyInput.value = String(qty);
      refresh();
    }),
  );
  (body.querySelector('#fp-unit') as HTMLSelectElement).addEventListener('change', (e) => {
    // מעבר יחידה שומר את הכמות הנוכחית: לגרם — שווה-הערך בגרמים; ליחידה בעלת שם — 1.
    // refQty מתעדכן גם כן — הצ'יפים תמיד יחסיים ליחידה שנבחרה עכשיו, לא לזו הקודמת.
    const curGrams = vals().grams;
    unitIdx = Number((e.target as HTMLSelectElement).value);
    qty = row.units[unitIdx].grams === 1 ? (curGrams ?? row.base.grams ?? 100) : 1;
    refQty = qty;
    qtyInput.value = String(qty);
    refresh();
  });

  const buildItem = (): EntryItem | null => {
    const v = vals();
    if (v.cal <= 0) { toast('הזיני כמות', 'error'); return null; }
    // שומרים את היחידה השמית שנבחרה (אם יש) — כדי שבפעם הבאה שהמאכל הזה ייפתח
    // (מהיסטוריה/תדירים) הוא יפתח שוב על אותה יחידה, לא יחזור ל"גרם"
    const namedUnit = !factorMode && row.units[unitIdx].grams > 1 ? row.units[unitIdx] : null;
    return {
      name_he: row.name,
      qty_desc: v.qtyDesc,
      grams: v.grams,
      calories: Math.round(v.cal),
      protein_g: round1(v.p),
      carbs_g: round1(v.c),
      fat_g: round1(v.fat),
      unit_grams: namedUnit?.grams,
      unit_name: namedUnit?.label,
      icon: row.food?.icon ?? undefined, // שורד להיסטוריה/מועדפים גם אם האייקון במאכל ישתנה אח"כ
      // מזהה המאכל (אישי/מאגר, אם יש) — כדי שנגיעה על השורה הזו מהיסטוריה/מועדפים תדע לחזור אליו לעריכה
      customFoodId: row.food?.custom ? row.food.id : undefined,
      foodCode: row.food && !row.food.custom ? row.food.code : undefined,
    };
  };

  // ✓ — במצב רגיל: הוספה ליומן וחזרה לחיפוש נקי; במצב עריכת-מנה: שמירת השינוי וסגירה.
  // epoch guard: אם המשתמשת ניווטה/סגרה בזמן השמירה (או דאבל-טאפ) — לא ממשיכים.
  tools.querySelector('#fp-add')!.addEventListener('click', () => {
    const item = buildItem();
    if (!item) return;
    if (edit) {
      if (busySaving) return;
      busySaving = true;
      const epEdit = flowEpoch;   // הריטריי מאריך את הפעולה — לא סוגרים מסך שכבר התחלף
      void updateEntry(edit.id, {
        meal_type: mealType,
        description: row.name,
        items: [item],
        calories: item.calories,
        protein_g: item.protein_g ?? null,
        carbs_g: item.carbs_g ?? null,
        fat_g: item.fat_g ?? null,
      }).then(() => {
        pendingRefresh = true;
        if (!flowOpen || epEdit !== flowEpoch) { onSavedCb?.(); return; }
        closeAddFlow();
        toast('המנה עודכנה ✓');
      }).catch((e: unknown) => {
        if (!flowOpen || epEdit !== flowEpoch) return;   // המשתמשת ניווטה בזמן הריטריי
        const transient = isTransient(e);
        reportError(e, { action: 'edit_food_entry', transient });
        const m = e instanceof Error ? e.message : '';
        const msg = m === 'not_found' ? 'הרישום כבר לא קיים — רועני'
          : m === 'no_session' ? 'הסשן פג — התחברי מחדש'
          : transient ? 'אין חיבור יציב — נסי שוב עוד רגע' : 'השמירה נכשלה — נסי שוב';
        toast(msg, 'error');
      }).finally(() => { busySaving = false; });
      return;
    }
    const ep = flowEpoch;
    void instantAdd([item], mealType, row.name, row.name).then((ok) => {
      if (!ok) return; // כישלון/כפילות — נשארים בעמוד, לא מנקים
      lastQuery = ''; // החיפוש מתנקה
      if (flowOpen && ep === flowEpoch) goBack();
    });
  });

  // ☆/★ — טוגל מועדפים: שומר את המאכל עם הכמות הנוכחית, או מסיר אם כבר מועדף (favId).
  // חשוב: לתפוס את הכפתור לפני ה-await (currentTarget מתאפס בסוף ה-dispatch הסינכרוני),
  // ולנעול (favBusy) לפני — נגד דאבל-טאפ שיוצר כפילות/מרוץ.
  const favBtn = tools.querySelector('#fp-fav') as HTMLButtonElement;
  favBtn.addEventListener('click', async () => {
    if (favBusy) return;
    favBusy = true;
    try {
      if (favId) {
        await removeFavorite(favId);
        favId = undefined;
        favBtn.textContent = '☆';
        favBtn.classList.remove('on');
        favBtn.setAttribute('aria-label', 'שמירה במועדפים');
        toast('הוסר מהמועדפים');
      } else {
        const item = buildItem();
        if (!item) return;
        favId = await addFavorite({
          name: row.name, items: [item], meal_type: mealType,
          calories: item.calories ?? 0, protein_g: item.protein_g ?? null,
          carbs_g: item.carbs_g ?? null, fat_g: item.fat_g ?? null,
        });
        favBtn.textContent = '★';
        favBtn.classList.add('on');
        favBtn.setAttribute('aria-label', 'הסרה מהמועדפים');
        toast('נשמר במועדפים ⭐');
      }
    } catch {
      toast('הפעולה נכשלה — נסי שוב', 'error');
    } finally {
      favBusy = false;
    }
  });

  // ✎ — עריכה: מאכל "שלי" לבעליו; מאכל מהמאגר — אדמין בלבד. `food` ברירת מחדל ל-row.food,
  // אבל ניתן להעביר מאכל שנשלף בנפרד (דרך מועדפים/היסטוריה בלי row.food מוכן — ראה למטה).
  const enableEdit = (food: Food = row.food!) => {
    const b = document.getElementById('fp-edit') as HTMLButtonElement | null;
    if (!b) return;
    b.hidden = false;
    b.disabled = false;
    b.setAttribute('aria-label', 'עריכת המאכל');
    b.onclick = () => navTo(() => showFoodEditPage(foodToRow(food)));
  };
  /** מאכל מהמאגר הלאומי, למשתמשת רגילה (לא אדמין) — שכפול לעריכה אישית במקום חסימה מוחלטת:
   *  יוצר עותק זהה ב"מאכל שלי" (אותם ערכים/יחידות, בלי אייקון קבוע) ופותח אותו לעריכה מיד.
   *  המאכל המקורי במאגר הלאומי לא משתנה עבור אף משתמשת אחרת. */
  const enableForkToEdit = (src: Food = row.food!) => {
    const b = document.getElementById('fp-edit') as HTMLButtonElement | null;
    if (!b) return;
    b.hidden = false;
    b.disabled = false;
    b.setAttribute('aria-label', 'שכפול לעריכה אישית');
    b.onclick = async () => {
      if (busySaving) return;
      busySaving = true;
      try {
        const newId = await addCustomFood({
          name_he: src.name_he, calories: src.calories,
          protein_g: src.protein_g, carbs_g: src.carbs_g, fat_g: src.fat_g,
          portions: src.portions,
        });
        const newFood: Food = { ...src, custom: true, id: newId, code: -1, icon: null };
        toast('נוצר עותק אישי לעריכה ✓');
        navTo(() => showFoodEditPage(foodToRow(newFood)));
      } catch {
        toast('השכפול נכשל — נסי שוב', 'error');
      } finally {
        busySaving = false;
      }
    };
  };
  /** מגיעים לשורה הזו ממועדפים/היסטוריה/תדירים — אין row.food מוכן, אבל הפריט שמור נושא
   *  customFoodId (אם המאכל המקורי היה אישי). שולפים את המאכל המלא בנגיעה ופותחים לעריכה,
   *  כדי שעריכה תעבוד מכל נקודת כניסה, לא רק מטאב "שלי". */
  const enableEditByRef = (customFoodId: string) => {
    const b = document.getElementById('fp-edit') as HTMLButtonElement | null;
    if (!b) return;
    b.hidden = false;
    b.disabled = false;
    b.setAttribute('aria-label', 'עריכת המאכל');
    b.onclick = async () => {
      if (busySaving) return;
      busySaving = true;
      try {
        const full = await fetchCustomFoodById(customFoodId);
        if (!full) { toast('המאכל נמחק — אי אפשר לערוך', 'error'); return; }
        navTo(() => showFoodEditPage(foodToRow(full)));
      } catch {
        toast('משהו השתבש — נסי שוב', 'error');
      } finally {
        busySaving = false;
      }
    };
  };
  /** כמו enableEditByRef, אבל למאכל מהמאגר הלאומי (foodCode על הפריט): שולפת את המאכל
   *  המלא ואז מפעילה עריכה-ישירה (אדמין) או שכפול-לעריכה (רגילה) בדיוק כמו הנתיב הישיר. */
  const enableEditByFoodCodeRef = (code: number) => {
    const b = document.getElementById('fp-edit') as HTMLButtonElement | null;
    if (b) { b.hidden = false; b.disabled = true; }
    void fetchNationalFoodByCode(code).then((food) => {
      if (!food) { if (b) b.hidden = true; return; }
      if (adminCache === true) enableEdit(food);
      else {
        enableForkToEdit(food);
        if (adminCache !== false) {
          void isAdmin().then((a) => { if (a) { adminCache = true; enableEdit(food); } });
        }
      }
    });
  };
  /** נפילה לאחור למועדפים/היסטוריה משנשמרו *לפני* שהוספנו customFoodId/foodCode לפריט —
   *  אין שום קישור שמור, רק שם. מנסים למצוא מאכל אישי עם אותו שם (חד-משמעי בלבד), ואם אין
   *  — מאכל מהמאגר הלאומי עם אותו שם (חד-משמעי גם כן). מציגים את הכפתור מיד במצב disabled
   *  (במקום hidden) כדי שלא "יקפוץ" באיחור — מעבר חלק מעמעם→פעיל, או חזרה ל-hidden אם לא
   *  נמצאה שום התאמה חד-משמעית באף אחד מהמאגרים. */
  const enableEditByNameFallback = (name: string) => {
    const b = document.getElementById('fp-edit') as HTMLButtonElement | null;
    if (b) { b.hidden = false; b.disabled = true; }
    void fetchCustomFoodByName(name).then((custom) => {
      if (custom) { enableEditByRef(custom.id!); return; }
      void fetchNationalFoodByName(name).then((national) => {
        if (!national) { if (b) b.hidden = true; return; }
        if (adminCache === true) enableEdit(national);
        else {
          enableForkToEdit(national);
          if (adminCache !== false) {
            void isAdmin().then((a) => { if (a) { adminCache = true; enableEdit(national); } });
          }
        }
      });
    });
  };
  if (row.food?.custom) enableEdit();
  else if (row.food) {
    if (adminCache === true) enableEdit();
    else {
      // ברירת מחדל מיידית: שכפול לעריכה (בטוח גם אם isAdmin עוד לא ענה). אם מתברר שהיא כן
      // אדמין — משדרגים לעריכה ישירה. לא שומרים false בקאש — כשל רשת לא ננעל לכל הסשן.
      enableForkToEdit();
      if (adminCache !== false) {
        void isAdmin().then((a) => { if (a) { adminCache = true; enableEdit(); } });
      }
    }
  } else if (row.base.customFoodId) {
    enableEditByRef(row.base.customFoodId);
  } else if (row.base.foodCode !== undefined) {
    enableEditByFoodCodeRef(row.base.foodCode);
  } else {
    enableEditByNameFallback(row.name);
  }
}

// ---------- מסך עריכת מאכל (בעלים/אדמין) ----------

function showFoodEditPage(row: RowFood): void {
  const f = row.food;
  if (!f) { goBack(); return; }
  const portions: FoodPortion[] = f.portions.filter((p) => p.grams > 0).map((p) => ({ ...p }));
  // אייקון: גם למאכל אישי וגם למאכל מהמאגר (מיגרציה 011 — לשני הטבלאות יש עמודת icon)
  let icon: string | null = f.icon ?? null;
  let iconOpen = false;

  const body = setPage('עריכת מאכל ✎', `
    <div class="card fp-card">
      <div class="fp-lbl">שם המאכל</div>
      <div id="fe-name-field"></div>
    </div>
    <div class="card fp-card">
      <div class="fp-lbl">ערכים ל-100 גרם</div>
      <div class="cf-grid">
        <label class="cf-label">קלוריות<input class="sheet-input" id="fe-cal" type="number" inputmode="decimal" min="0" value="${f.calories}"></label>
        <label class="cf-label">חלבון (ג')<input class="sheet-input" id="fe-p" type="number" inputmode="decimal" min="0" value="${f.protein_g ?? ''}"></label>
        <label class="cf-label">פחמימות (ג')<input class="sheet-input" id="fe-c" type="number" inputmode="decimal" min="0" value="${f.carbs_g ?? ''}"></label>
        <label class="cf-label">שומן (ג')<input class="sheet-input" id="fe-f" type="number" inputmode="decimal" min="0" value="${f.fat_g ?? ''}"></label>
      </div>
    </div>
    <div class="card fp-card">
      <div class="fp-lbl">יחידות ומנות</div>
      <div id="fe-units"></div>
      <button class="btn-link" id="fe-uadd">➕ הוספת יחידה</button>
    </div>
    <div class="sheet-err" id="sheet-err"></div>`);

  // כותרת: ✓ שומר
  const tools = document.getElementById('page-head-tools')!;
  tools.innerHTML = `<button class="ha go" id="fe-save" aria-label="שמירה">✓</button>`;

  // שדה שם + בורר אייקון — זהה למאכל אישי ולמאכל מהמאגר (שניהם נערכים כאן)
  const renderIconField = () => {
    const box = document.getElementById('fe-name-field')!;
    const prevInput = document.getElementById('fe-name') as HTMLInputElement | null;
    const nameVal = prevInput ? prevInput.value : f.name_he;
    const parts = iconPickerParts(icon, nameVal, iconOpen);
    box.innerHTML = `
      <div class="name-row">
        ${parts.btn}
        <input class="sheet-input" id="fe-name" type="text" value="${escapeHtml(nameVal)}">
      </div>
      ${parts.panel}`;
    wireIconPicker(box,
      () => { iconOpen = !iconOpen; renderIconField(); },
      (val) => { icon = val; iconOpen = false; renderIconField(); },
    );
  };
  renderIconField();

  // יחידות עריכות במקום: שם + משקל בכל שורה (input חי מעדכן את portions), ✕ מסיר
  const renderUnits = () => {
    const box = document.getElementById('fe-units')!;
    box.innerHTML = portions.length
      ? portions.map((p, i) => `
          <div class="unit-row unit-row-edit">
            <input class="sheet-input ue-name" data-un="${i}" type="text" value="${escapeHtml(p.name_he)}" placeholder="פרוסה / כף">
            <input class="sheet-input ue-grams" data-ug="${i}" type="number" inputmode="decimal" min="0" value="${p.grams || ''}" placeholder="30">
            <span class="ug">ג'</span>
            <button class="urm" data-prm="${i}" aria-label="הסרה">✕</button>
          </div>`).join('')
      : `<div class="muted" style="padding:4px 2px">אין יחידות — הוסיפי (למשל "פרוסה = 25 ג'")</div>`;
    box.querySelectorAll<HTMLInputElement>('[data-un]').forEach((inp) =>
      inp.addEventListener('input', () => { portions[Number(inp.dataset.un)].name_he = inp.value.trim(); }),
    );
    box.querySelectorAll<HTMLInputElement>('[data-ug]').forEach((inp) =>
      inp.addEventListener('input', () => { portions[Number(inp.dataset.ug)].grams = Number(inp.value) || 0; }),
    );
    box.querySelectorAll<HTMLButtonElement>('[data-prm]').forEach((b) =>
      b.addEventListener('click', () => { portions.splice(Number(b.dataset.prm), 1); renderUnits(); }),
    );
  };
  renderUnits();

  body.querySelector('#fe-uadd')!.addEventListener('click', () => {
    portions.push({ name_he: '', grams: 0 });
    renderUnits();
    // פוקוס על שם היחידה החדשה
    const inputs = document.querySelectorAll<HTMLInputElement>('#fe-units [data-un]');
    inputs[inputs.length - 1]?.focus();
  });

  tools.querySelector('#fe-save')!.addEventListener('click', async () => {
    const val = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
    const num = (id: string) => { const v = val(id); return v ? Number(v) : null; };
    const name = val('fe-name');
    const cal = Number(val('fe-cal'));
    if (name.length < 2) { document.getElementById('sheet-err')!.textContent = 'צריך שם למאכל'; return; }
    if (!Number.isFinite(cal) || cal <= 0) { document.getElementById('sheet-err')!.textContent = 'הזיני קלוריות ל-100 גרם'; return; }
    if (busySaving) return;
    busySaving = true;
    const patch: FoodPatch = {
      name_he: name, calories: cal,
      protein_g: num('fe-p'), carbs_g: num('fe-c'), fat_g: num('fe-f'),
      portions: portions.filter((p) => p.name_he && p.grams > 0), // שורות ריקות לא נשמרות
      icon, // לשני הטבלאות (אישי/מאגר) יש עמודת icon מאז מיגרציה 011
    };
    const ep = flowEpoch; // אם ניווטו/סגרו בזמן השמירה — לא מציירים על מסך אחר
    try {
      if (f.custom && f.id) await updateCustomFood(f.id, patch);
      else await updateNationalFood(f.code, patch);
      toast('המאכל עודכן ✓');
      if (!flowOpen || ep !== flowEpoch) return;
      // חזרה לעמוד המאכל עם הערכים המעודכנים. המחסנית: [...חיפוש, עמוד-ישן, עריכה] —
      // מסירים גם את עמוד-המאכל הישן שמתחת, שחזרה לא תציג ערכים ישנים.
      const updated: Food = { ...f, ...patch };
      if (stack.length >= 2) stack.splice(stack.length - 2, 1); // העמוד הישן שמתחת לעריכה
      replaceWith(() => showFoodPage(foodToRow(updated)));
    } catch {
      const err = document.getElementById('sheet-err');
      if (err) err.textContent = 'השמירה נכשלה — נסי שוב';
    } finally {
      busySaving = false;
    }
  });
}

// ---------- חלונית בחירת פריטים מארוחה (bottom sheet בתוך העמוד) ----------

function openMealPicker(entry: Entry, label: string): void {
  document.getElementById('meal-picker')?.remove();
  const page = pageOverlay();
  if (!page) return;
  const items = entryToItems(entry).map((i) => ({ ...i }));
  const checked = items.map(() => true);
  let mealType: MealType = forcedMeal ?? ((entry.meal_type ?? guessMealType()) as MealType);

  const overlay = document.createElement('div');
  overlay.id = 'meal-picker';
  const itemRows = items.map((it, i) => `
    <div class="pitem" data-pi="${i}">
      <span class="pchk">✓</span>
      <span class="pem">${it.icon || foodEmoji(it.name_he)}</span>
      <span class="pn">${escapeHtml(it.name_he)}${it.qty_desc ? `<small>${escapeHtml(it.qty_desc)}</small>` : ''}</span>
      <span class="pk">${fmt(it.calories ?? 0)}</span>
    </div>`).join('');
  const mealChips = () => (Object.keys(MEAL_LABEL) as MealType[])
    .map((m) => `<button class="mc${m === mealType ? ' on' : ''}" data-mpm="${m}">${MEAL_LABEL[m]}</button>`)
    .join('');
  overlay.innerHTML = `
    <div class="mp-sheet">
      <div class="handle"></div>
      <h4>${escapeHtml(label)}</h4>
      <div class="psub">בחרי מה להוסיף ליומן:</div>
      ${itemRows}
      <div class="psum"><span id="mp-count"></span><span id="mp-cal"></span></div>
      <div class="mchips" id="mp-meals">${mealChips()}</div>
      <button class="qadd" id="mp-add">הוספה ליומן ✓</button>
    </div>`;
  page.appendChild(overlay);

  const refresh = () => {
    const sel = items.filter((_, i) => checked[i]);
    const cal = sel.reduce((s, it) => s + (it.calories ?? 0), 0);
    (overlay.querySelector('#mp-count') as HTMLElement).textContent = `${sel.length} נבחרו`;
    (overlay.querySelector('#mp-cal') as HTMLElement).textContent = `${fmt(cal)} קק"ל`;
    (overlay.querySelector('#mp-add') as HTMLButtonElement).disabled = sel.length === 0;
    (overlay.querySelector('#mp-meals') as HTMLElement).innerHTML = mealChips();
    overlay.querySelectorAll<HTMLButtonElement>('[data-mpm]').forEach((b) =>
      b.addEventListener('click', () => { mealType = b.dataset.mpm as MealType; refresh(); }),
    );
  };
  refresh();

  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); }); // נגיעה ברקע סוגרת
  overlay.querySelectorAll<HTMLElement>('[data-pi]').forEach((el) =>
    el.addEventListener('click', () => {
      const i = Number(el.dataset.pi);
      checked[i] = !checked[i];
      el.querySelector('.pchk')!.classList.toggle('off', !checked[i]);
      refresh();
    }),
  );
  overlay.querySelector('#mp-add')!.addEventListener('click', () => {
    const sel = items.filter((_, i) => checked[i]);
    if (!sel.length) return;
    overlay.remove();
    const desc = sel.map((i) => i.name_he).join(', ');
    void instantAdd(sel, mealType, desc, label).then((ok) => { if (ok) clearSearchQuery(); });
  });
}

// ============================================================
// 8. שקילת בוקר
// ============================================================

/** YYYY-MM-DD מקומי → Date מקומי (לא new Date(str) — נחתך יום אחורה באזורי זמן שליליים) */
function parseLocalDate(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function showWeighIn(): void {
  const body = setPage('שקילת בוקר ⚖️', `
    <p class="cf-hint" style="text-align:center">הכניסי את המשקל של הבוקר (על קיבה ריקה, מומלץ).</p>
    <div class="weight-row">
      <input class="sheet-input weight-input" id="weight-val" type="number" inputmode="decimal" step="0.1" min="35" max="250" placeholder="67.8">
      <span class="weight-unit">ק"ג</span>
    </div>
    <button type="button" class="weight-hint" id="weight-hint" hidden></button>
    <button class="btn-primary" id="weight-save">שמירה</button>
    <div class="sheet-err" id="sheet-err"></div>`);
  const input = body.querySelector<HTMLInputElement>('#weight-val')!;
  const hint = body.querySelector<HTMLButtonElement>('#weight-hint')!;
  input.focus();

  // מילוי-מראש אם כבר יש שקילה ליום הזה; אחרת רמז על המשקל האחרון (לחיץ להעתקה)
  void (async () => {
    try {
      const existing = await fetchWeightForDate(targetDay);
      if (existing != null) {
        input.value = existing.toFixed(1);
        return;
      }
      const last = await fetchLastWeightBefore(targetDay);
      if (!last) return;
      const dateTxt = new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'numeric' }).format(parseLocalDate(last.date));
      hint.innerHTML = `המשקל האחרון שהוזן: <b>${last.weightKg.toFixed(1)} ק"ג</b> (${dateTxt})`;
      hint.hidden = false;
      hint.addEventListener('click', () => { input.value = last.weightKg.toFixed(1); input.focus(); });
    } catch { /* לא קריטי — השדה פשוט נשאר ריק */ }
  })();

  const save = async () => {
    const v = Number(input.value);
    if (!Number.isFinite(v) || v < 35 || v > 250) {
      document.getElementById('sheet-err')!.textContent = 'משקל בין 35 ל-250 ק"ג';
      return;
    }
    try {
      await saveWeightForDate(Math.round(v * 10) / 10, targetDay);
      pendingRefresh = true;
      closeAddFlow();
      toast('המשקל נשמר ✓');
    } catch {
      document.getElementById('sheet-err')!.textContent = 'השמירה נכשלה — נסי שוב';
    }
  };
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') void save(); });
  body.querySelector('#weight-save')!.addEventListener('click', () => void save());
}

// ============================================================
// 8. "התפריט שלי" — בונה-ארוחה מהרכיבים שהמלווה הרכיב (בלי AI, בלי רשת)
// ============================================================

/**
 * מסך התפריט: טאב לכל ארוחה, רכיבים עם סטפר, וסרגל חי שמראה את הסכום
 * מתעדכן תוך כדי הבחירה — "המשחק עם הקלוריות". קיצורי "אופציה 1/2" מסמנים
 * צירוף שלם בהקשה, ומשם אפשר להוריד/להחליף רכיב ולראות מיד מה זה עושה.
 * ההוספה עוברת ב-instantAdd, כך שכל רכיב נרשם כשורה נפרדת בבית — כמו בכל
 * שאר הזרימות.
 */
function showMealPlan(plan: MealPlan): void {
  const wanted = forcedMeal ?? guessMealType();
  let secIdx = Math.max(0, plan.sections.findIndex((s) => s.meal === wanted));
  let sel: Selection = {};
  /** מה כבר נאכל היום + היעדים — נטען פעם אחת ומצויר בסרגל כשמגיע */
  let eaten: DayTotals | null = null;
  let targets: DailyTargets | null = null;
  let loadedDay = false;
  /** הטעינה נכשלה (רשת/הרשאה) — שונה מ"אין יעד": אסור להציג מספר שנראה אמיתי */
  let loadFailed = false;
  /** נעילת ההוספה בזמן ה-insert, כדי שהכפתור לא ייראה פעיל ויבלע הקשה שנייה */
  let adding = false;
  /** מונה בקשות ל-loadDay — שתי טעינות מקבילות, והאחרונה שחוזרת מנצחת במקום
   *  האחרונה שיצאה (flowEpoch לא זז כשנשארים באותו מסך, ולכן אינו מספיק) */
  let dayReq = 0;

  const section = (): PlanSection => plan.sections[secIdx];

  /** שורת "נשאר להיום" — שלד בזמן טעינה, כדי שהסרגל לא יקפוץ כשהנתונים מגיעים */
  function remainingHtml(t: PlanTotals): string {
    if (!loadedDay) return `<div class="mp-left"><span class="mp-skel"></span></div>`;
    // אין נתונים ≠ 0: שליפה שנכשלה הייתה מציגה את כל היעד היומי כ"נשאר",
    // והמשתמשת הייתה בונה ארוחה על מספר שקרי. (אותו כלל שתוקן ב-viewer.ts.)
    if (loadFailed) return `<div class="mp-left muted">לא הצלחנו לחשב כמה נשאר</div>`;
    if (!targets) return `<div class="mp-left muted">אין יעד יומי בהגדרות</div>`;
    const cal = targets.calories - (eaten?.calories ?? 0) - t.calories;
    const pro = targets.proteinG - (eaten?.proteinG ?? 0) - t.proteinG;
    const calTxt = cal >= 0 ? `${fmt(cal)} קק"ל` : `חריגה ${fmt(-cal)}`;
    const proTxt = pro >= 0 ? `${fmt(pro)} ג׳ חלבון` : `מעל היעד`;
    return `<div class="mp-left">
      <span class="mp-left-t">${t.calories > 0 ? 'יישאר להיום' : 'נשאר להיום'}</span>
      <span class="mp-left-v${cal < 0 ? ' over' : ''}">${calTxt} · ${proTxt}</span>
    </div>`;
  }

  function compHtml(c: PlanComponent): string {
    const qty = sel[c.id] ?? 0;
    const on = qty > 0;
    const max = maxQty(c);
    const macro = c.free
      ? 'חופשי'
      : `${fmt(c.calories)} קק"ל${c.protein_g ? ` · ${round1(c.protein_g)} ג׳ חלבון` : ''}`;
    // כל שדה מה-JSON שנכנס ל-HTML מנוקה — כולל המזהה, שיושב בתוך ערך תכונה:
    // מזהה עם מרכאה שבר את data-mpt והשורה הפסיקה להגיב ללחיצות בשקט.
    const id = escapeHtml(c.id);
    // סטפר רק לרכיב שניתן לשכפל ושכבר מסומן — אחרת כפתור סימון.
    // הכפתורים הם <button> אמיתי ולא div עם role: אחרת המקלדת מפעילה את השורה
    // העוטפת במקום את ה-±, וסימון מתבטל בדיוק כשמנסים להוסיף מנה.
    const stepper = on && max > 1
      ? `<div class="mp-step">
           <button class="mp-sbtn" data-mpq="${id}" data-d="-1" aria-label="פחות">−</button>
           <span class="mp-qty">${qty}</span>
           <button class="mp-sbtn" data-mpq="${id}" data-d="1" aria-label="עוד"${qty >= max ? ' disabled' : ''}>+</button>
         </div>`
      : `<button class="mp-check" data-mpcheck="${id}" aria-pressed="${on}"
           aria-label="${escapeHtml(c.name)}">${on ? '✓' : ''}</button>`;
    return `<div class="mp-row${on ? ' on' : ''}" data-mpt="${id}">
      <span class="mp-ic">${escapeHtml(c.icon ?? '🍽️')}</span>
      <div class="mp-mid">
        <span class="mp-name">${escapeHtml(c.name)}${c.qty && !c.free ? ` <span class="mp-q">${escapeHtml(c.qty)}</span>` : ''}</span>
        <span class="mp-macro">${macro}${c.note ? ` · ${escapeHtml(c.note)}` : ''}</span>
      </div>
      ${stepper}
    </div>`;
  }

  function render(): void {
    const s = section();
    const t = selectionTotals(s, sel);
    const picks = countedPicks(s, sel);

    const tabs = plan.sections
      .map((sec, i) => `<button class="mp-tab${i === secIdx ? ' on' : ''}" data-mpsec="${i}">${escapeHtml(sec.icon ?? '')} ${escapeHtml(sec.title.replace('ארוחת ', ''))}</button>`)
      .join('');

    const opts = (s.options ?? [])
      .map((o) => `<button class="mp-opt${optionIsActive(s, sel, o) ? ' on' : ''}" data-mpopt="${escapeHtml(o.id)}">${escapeHtml(o.name)}</button>`)
      .join('');

    const groups = s.groups
      .map((g) => `<div class="mp-group">
        ${g.title ? `<div class="mp-gtitle">${escapeHtml(g.title)}</div>` : ''}
        ${g.components.map(compHtml).join('')}
      </div>`)
      .join('');

    const body = setPage(plan.title, `
      <div class="mp-bar">
        <div class="mp-sum">
          <span class="mp-cal">${fmt(t.calories)}</span>
          <span class="mp-unit">קק"ל</span>
          <span class="mp-pro">${round1(t.proteinG)} ג׳ חלבון</span>
        </div>
        ${remainingHtml(t)}
      </div>
      <div class="mp-tabs">${tabs}</div>
      ${opts ? `<div class="mp-opts">${opts}</div>` : ''}
      ${s.hint ? `<div class="mp-hint">${escapeHtml(s.hint)}</div>` : ''}
      ${groups}
      <div class="mp-foot muted">${escapeHtml(plan.daily.note ?? '')}</div>`);

    // ✓ בכותרת — כמו במסך האישור: נשאר בהישג יד גם כשגוללים רשימה ארוכה
    const tools = document.getElementById('page-head-tools')!;
    tools.innerHTML = `<button class="ha go" id="mp-plan-add" aria-label="הוספה ליומן"${picks === 0 || adding ? ' disabled' : ''}>✓</button>`;
    tools.querySelector('#mp-plan-add')!.addEventListener('click', () => void addSelection());

    body.querySelectorAll<HTMLElement>('[data-mpsec]').forEach((b) =>
      b.addEventListener('click', () => { secIdx = Number(b.dataset.mpsec); sel = {}; render(); }),
    );
    body.querySelectorAll<HTMLElement>('[data-mpopt]').forEach((b) =>
      b.addEventListener('click', () => {
        const o = (section().options ?? []).find((x) => x.id === b.dataset.mpopt);
        if (!o) return;
        // הקשה שנייה על אופציה פעילה מנקה — כדי שאפשר יהיה להתחיל מדף חלק
        sel = optionIsActive(section(), sel, o) ? {} : applyOption(section(), o);
        render();
      }),
    );
    // סטפר לפני השורה — stopPropagation כדי ש-± לא יבטל את הסימון
    body.querySelectorAll<HTMLElement>('[data-mpq]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        sel = changeQty(section(), sel, b.dataset.mpq!, Number(b.dataset.d));
        render();
      }),
    );
    const toggleId = (id: string) => { sel = toggleComponent(section(), sel, id); render(); };
    body.querySelectorAll<HTMLElement>('[data-mpcheck]').forEach((b) =>
      b.addEventListener('click', (e) => { e.stopPropagation(); toggleId(b.dataset.mpcheck!); }),
    );
    body.querySelectorAll<HTMLElement>('[data-mpt]').forEach((row) =>
      row.addEventListener('click', () => toggleId(row.dataset.mpt!)),
    );
  }

  async function addSelection(): Promise<void> {
    const s = section();
    const items = selectionToItems(s, sel);
    if (!items.length || adding) return;
    // ה-await חוצה מסכים: בלי שמירת ה-epoch, render() היה מצייר על המסך שאליו
    // המשתמשת ניווטה בינתיים — ואם הזרימה נסגרה, setPage היה מחייה עמוד שנסגר.
    const ep = flowEpoch;
    adding = true;
    render();
    const ok = await instantAdd(items, forcedMeal ?? s.meal, s.title, s.title);
    adding = false;
    if (!flowOpen || ep !== flowEpoch) return;
    if (!ok) { render(); return; }
    sel = {};
    // מה שנרשם נכנס לחשבון "נשאר להיום" — מרעננים את סכום היום
    eaten = null;
    loadedDay = false;
    render();
    void loadDay();
  }

  /** יעדי היום + מה שכבר נאכל בו. כישלון מסומן במפורש ואינו מוצג כ-0. */
  async function loadDay(): Promise<void> {
    const ep = flowEpoch;
    const req = ++dayReq;
    let failed = false;
    let tg: DailyTargets | null = null;
    let entries: Entry[] = [];
    try {
      [tg, entries] = await Promise.all([fetchTargets(), fetchEntriesForDate(targetDay)]);
    } catch {
      failed = true;  // רשת/הרשאה — לא "אין יעד" ולא "אכלה 0"
    }
    if (!flowOpen || ep !== flowEpoch || req !== dayReq) return;
    loadFailed = failed;
    targets = failed ? null : tg;
    eaten = failed ? null : sumTotals(entries);
    loadedDay = true;
    render();
  }

  render();
  void loadDay();
}
